/**
 * The Xero integration's shared model — one shape for every syncable entity.
 *
 * The link is modelled ONCE, as an envelope every syncable record carries,
 * read through the helpers here, so a fifth entity is a registry entry, not a
 * new component.
 *
 * ## ⚠️ `xeroQuoteId` IS NEVER THE TEST. Read this before writing a gate
 *
 * **`d_quotes.xero_quote_id` is writable and NOTHING WRITES IT** (confirmed by
 * the backend against their own source; `docs/api/quotes.md` gotcha 6). A push
 * writes `xero_quote_number` and the link row, never that column. So
 * `xeroQuoteId === null` does not mean "not in Xero" — it answers null forever,
 * including for a quote demonstrably in Xero, which makes it the most dangerous
 * kind of signal: always available, always parseable, always wrong the same way.
 *
 * The order to ask in:
 *
 * 1. **The link envelope is authoritative.** {@link linkOf} → `id` is the Xero
 *    GUID, out of `f_xero_links`, and `status` is the whole state.
 * 2. **`xeroQuoteNumber` is the fallback**, and only that. The server writes it
 *    after Xero accepts the document, so non-null does prove the quote reached
 *    Xero — but it carries no GUID, cannot express `failed`, `conflict` or
 *    `archived`, and a user can type into the field by hand.
 * 3. **`xeroQuoteId` is never a test**, in either direction.
 *
 * ⚠️ **A quote READ still carries no `xero` key** (`docs/api/xero.md`), so on
 * `/quotes/{id}` rule 1 has nothing to read yet and rule 2 is all there is
 * until the backend embeds the envelope.
 *
 * **Three places can hold a Xero quote reference and only one is authoritative:**
 * `d_quotes.xero_quote_id` (empty, always), `d_deals.xero_quote_id` (the same
 * relationship recorded from the other side, retained by product decision, and
 * the two may drift) and `f_xero_links` (the real one). The backend's intended
 * end state across the three is undecided — recorded here rather than resolved,
 * because picking one unilaterally on this side is how a client ends up
 * enforcing a model the server never agreed to.
 *
 * ## The thing that is NOT negotiable
 *
 * **Nothing in this app ever talks to `api.xero.com`.** Xero is OAuth2 with a
 * confidential client: there is a client secret, and the refresh token rotates.
 * A browser cannot hold either — a token in `localStorage` is readable by any
 * script on the origin, and shipping the secret in a JS bundle publishes it.
 * The sync engine, token store, webhook receiver and rate limiter all live in
 * `emerge-backend-app`. This file describes what the frontend reads back from
 * OUR API, and nothing more.
 *
 * ## What the live tenant actually looks like
 *
 * Measured against SEED Global Education Limited (UK, base currency GBP):
 *
 * - **938+ contacts**, of which only ~70 are customers. The rest are expense
 *   vendors — hotels, taxi firms, a printing press, staff filing expense
 *   claims. A bulk import of "all Xero contacts" fills the CRM with Dubai Taxi.
 *   `isLikelyCustomer` on a match is the backend's answer to that.
 * - **Only 18% of contacts carry an email**, so email can never be the primary
 *   match key.
 * - Contacts are **school-level, not university-level**: "Boston University -
 *   Questrom School of Business", "Boston University - College of Arts &
 *   Sciences" and "Trustees of Boston University" are three separate contacts.
 *   That is correct for how SEED sells and must not be "deduplicated".
 * - Genuine duplicates exist anyway — "Bocconi University" and "Gud Bocconi"
 *   are one customer. Which is why {@link XeroSyncStatus} has a `conflict`
 *   state and why linking is a human decision, not a name comparison.
 */

/*
 * Type-only, and it must stay that way: `api.ts` imports `normaliseSummary`
 * from this file at runtime, so a value import back would be a real cycle.
 * `import type` is erased at build time, so nothing circular survives.
 */
import type { ApiError } from './api';

/**
 * Where a record stands with Xero. Six states, because collapsing any two of
 * them loses the thing the operator needs to know.
 *
 * - `unlinked`  — no Xero counterpart, and nobody has asked for one. The
 *                 resting state for most records; not an error.
 * - `pending`   — queued to push. Set the instant the record is saved, so the
 *                 CRM write never blocks on Xero being reachable.
 * - `synced`    — linked, and our last push was accepted.
 * - `failed`    — the push was refused. `error` says why and the operator can
 *                 retry; this is the ONLY state with a retry affordance.
 * - `conflict`  — both sides changed since the last sync. Never resolved
 *                 automatically: Xero holds the filed accounts, and silently
 *                 overwriting a figure an accountant entered is the one
 *                 unrecoverable mistake this integration can make.
 * - `archived`  — the Xero counterpart must not be written to, and this is NOT
 *                 the same as unlinked: the id is still recorded, so treating it
 *                 as unlinked and creating afresh duplicates the record in an
 *                 accounting system.
 *
 * ⚠️ **`archived` carries TWO situations, and only `error.code` tells them
 * apart.** One is a counterpart still in Xero and archived there. The other is
 * a counterpart **DELETED** in Xero: `XERO_QUOTE_GONE`, `XERO_INVOICE_GONE` and
 * `XERO_CONTACT_GONE` all "stand the link down" by setting status to
 * `archived`, and the backend's `updateStatus` never clears `xero_id` — so the
 * link keeps a GUID that now addresses nothing. Both share the instruction "do
 * not write to this"; they do NOT share a sentence. Saying "the record in Xero
 * is archived" about a deleted one sends somebody to Xero to look for a
 * document that is not there. Use {@link isStoodDownAsGone}.
 */
export type XeroSyncStatus = 'unlinked' | 'pending' | 'synced' | 'failed' | 'conflict' | 'archived';

/**
 * The envelope every syncable record carries, under a `xero` key.
 *
 * Deliberately identical across companies, contacts, quotes and invoices, so
 * `<XeroPanel>` and `<XeroStatusBadge>` are written once. If the backend stores
 * this in one `xero_links(entity_type, entity_id, …)` table rather than columns
 * per entity, adding a fifth entity costs this app nothing at all.
 *
 * ---
 *
 * ⚠️ **THE NAMING TRAP: the backend has TWO envelopes, and this is the record
 * one.** They differ on the single field most likely to be read without
 * checking, and the wrong one is a plausible number rather than a crash.
 *
 * | backend type | where it is served | `id` is |
 * | --- | --- | --- |
 * | `XeroEnvelope` (`xero-envelope.ts`) | embedded on a record — `GET /quotes/{id}`, `/invoices`, `/companies` | **the Xero GUID** |
 * | `XeroLinkEnvelope` (`xero-links.service.ts`) | the operator endpoints — `/xero/links` | **the `f_xero_links` row PK**, with the GUID under `xeroId` |
 *
 * **`XeroLink` models `XeroEnvelope`, the first row.** Our name matches the
 * second one, which is the trap: anyone wiring `/xero/links` will reach for the
 * type whose name is closest and get an interface whose `id` means something
 * else entirely. Serializing a link row's primary key where a GUID is expected
 * looks like an id, reads like an id, and addresses nothing in Xero — the
 * backend says as much in `toEnvelope`'s own comment, which is why it maps
 * `envelope.xeroId` and not `envelope.id`.
 *
 * So `/xero/links` needs its **own** interface, not a widened or reused
 * `XeroLink`. `XeroLinkEnvelope` also carries `observedAtXero` inside `error`,
 * which the record embed does not — see {@link XeroLinkError}.
 */
export interface XeroLink {
  /**
   * The Xero GUID. `null` in every state except `synced`, `conflict` and
   * `archived` — a `pending` record has not been given one yet.
   *
   * **This is the authoritative Xero id, and the only one.** It comes off
   * `f_xero_links`, not off any `xero*Id` column on the record — see the
   * `xeroQuoteId` warning in this file's header before writing a gate against
   * one of those.
   */
  id: string | null;
  status: XeroSyncStatus;
  /** When OUR push was last accepted. Null until the first success. */
  lastSyncedAt: string | null;
  /**
   * Xero's own `UpdatedDateUTC` for the counterpart, as of our last read. This
   * is the whole basis of conflict detection: if it has moved since
   * `lastSyncedAt`, someone edited the record inside Xero.
   */
  updatedAtXero: string | null;
  /**
   * Why the last attempt failed. Present only for `failed` and `conflict`.
   *
   * `code` is switched on, `message` is shown. Never parse `message` — Xero's
   * validation prose is not stable, and production flattens it.
   */
  error: XeroLinkError | null;
}

export interface XeroLinkError {
  /**
   * ⚠️ **NULLABLE.** The wire shape is
   * `{ code: string | null; message: string | null } | null` — read off
   * `XeroEnvelope` in the backend's `xero-envelope.ts`, which is the record
   * embed this interface models. `error.code.toUpperCase()` or
   * `error.message.length` can throw at runtime even though `tsc` is green if
   * either is declared non-null.
   *
   * ⚠️ **`null` here means "Xero gave no reason", which is NOT the same claim as
   * an empty string.** Do not launder either field through `?? ''`: a blank
   * message rendered in a banner tells the reader Xero answered and said
   * nothing, when in fact nothing was recorded at all. Branch on the null and
   * say which happened, or say nothing — an absent reason is a real state on a
   * `failed` link, not a formatting problem.
   *
   * `code` is switched on and must fall through to the server's own message when
   * it is unrecognised; a `null` code is by definition unrecognised, so it takes
   * that same path rather than a new one.
   */
  code: string | null;
  message: string | null;
  /**
   * ⚠️ **NOT on the record embed.** The backend's `XeroEnvelope` — the object a
   * quote, invoice or company carries under `xero` — has exactly `id`, `status`,
   * `lastSyncedAt`, `updatedAtXero` and a two-field `error`. This key exists on
   * `XeroLinkEnvelope`, the operator shape behind `/xero/links`, and reaches
   * this interface only if something is ever wired against that. Optional is
   * therefore the correct declaration and it stays optional; a reader must treat
   * `undefined` as "this endpoint does not send it", never as "there was no
   * drift".
   *
   * On `XERO_DRIFT`, Xero's own newer timestamp — the change that blocked the
   * push. Null on every other error, and cleared when the conflict resolves.
   *
   * It exists so the two timestamps can be READ rather than parsed back out of
   * `message`. Prose is not a contract: its wording belongs to the server and
   * a client scraping it breaks silently the day someone rewords it.
   *
   * **This is deliberately NEWER than the link's `updatedAtXero`**, which is
   * held at the last agreed baseline. Advancing the baseline here would make
   * the next push find no drift and overwrite the very edit this reported — the
   * check would disarm itself in the act of firing.
   */
  observedAtXero?: string | null;
  /**
   * When Xero refused specific fields. Xero returns these per element rather
   * than per path, so this is a flat list rather than the `{field: message}`
   * map `ApiError.fieldErrors` produces for our own 422s.
   *
   * ⚠️ Like `observedAtXero`, this is **not on the record embed** either — the
   * per-element detail arrives on the `ApiError` from a push, where
   * {@link xeroErrorLines} handles it. Optional, and `undefined` means "not
   * sent", never "Xero named no fields".
   */
  details?: readonly string[];
}

/** A record that may carry a link. Everything here accepts a bare object. */
export interface MaybeLinked {
  xero?: XeroLink | null;
}

/**
 * The `unlinked` reading for a record whose backend does not yet send a `xero`
 * key at all.
 *
 * This matters more than it looks: the integration ships to the frontend before
 * the backend fills the field in, and an `undefined` link must read as "no
 * counterpart", never as a crash or an empty badge. Every helper below routes
 * through this.
 */
export const NO_LINK: XeroLink = {
  id: null,
  status: 'unlinked',
  lastSyncedAt: null,
  updatedAtXero: null,
  error: null,
};

export function linkOf(record: MaybeLinked | null | undefined): XeroLink {
  return record?.xero ?? NO_LINK;
}

/**
 * The six link statuses, as an exhaustive map rather than an array.
 *
 * A `Record<XeroSyncStatus, true>` fails to compile the day a seventh status is
 * added to the union; a `string[]` would go on quietly rejecting it as
 * malformed and reporting a linked record as unlinked. The failure that matters
 * here is the silent one.
 */
const XERO_LINK_STATUSES: Record<XeroSyncStatus, true> = {
  unlinked: true,
  pending: true,
  synced: true,
  failed: true,
  conflict: true,
  archived: true,
};

/**
 * Is this value the link envelope?
 *
 * It checks the two fields anything downstream actually SWITCHES on — `status`,
 * which decides every affordance offered, and `id`, which is the Xero GUID and
 * the thing that tells "linked" from "never pushed". The three remaining keys
 * are display-only and a wrong one costs a rendered dash; a wrong `status` costs
 * a duplicate document inside SEED's accounting system.
 */
export function isXeroLink(value: unknown): value is XeroLink {
  if (typeof value !== 'object' || value === null) return false;
  if (!('status' in value) || !('id' in value)) return false;
  const { status, id } = value;
  if (typeof status !== 'string' || !Object.hasOwn(XERO_LINK_STATUSES, status)) return false;
  return id === null || typeof id === 'string';
}

/*
 * ⚠️ **`readXeroLink` USED TO LIVE HERE AND IS DELETED.** It read the envelope
 * off a `record: object` with a runtime `'xero' in record` test, and it existed
 * for exactly one reason: `Quote` and `Invoice` in `src/lib/types.ts` did not
 * declare the key, so a caller could not reach it through the type system.
 * Four files carried byte-identical copies of that workaround.
 *
 * Both interfaces now declare `xero` as a REQUIRED {@link XeroLink}, so the
 * reason is gone and {@link linkOf} is the single reader. Do not reintroduce an
 * `in`-test variant: it takes a wider parameter type, so a caller passing a
 * record that carries no link — a kanban card, say, whose board projection
 * selects no link data — would compile and silently read as unlinked, which on
 * the create branch is an offer to make a second document in Xero.
 *
 * {@link isXeroLink} below survives as a general runtime guard for a value of
 * unknown provenance. It has NO callers in this repo today; that is not a
 * mistake to fix by finding one.
 */

/* -------------------------------------------------------------------------
 * The entity registry
 *
 * The single place that knows which entities sync, and the only place a fifth
 * one gets added. `slug` is what the backend's `/xero/...` routes take, so it
 * is also the `entity_type` in the link table.
 * ---------------------------------------------------------------------- */

export type XeroEntity = 'companies' | 'contacts' | 'quotes' | 'invoices';

export interface XeroEntityMeta {
  slug: XeroEntity;
  /** Singular, for "Sync this company to Xero". */
  label: string;
  plural: string;
  /**
   * What this maps to on the Xero side, in Xero's own vocabulary. Shown in the
   * UI because "Company → Xero Contact" is genuinely confusing the first time
   * and staff will otherwise assume our Contacts map to Xero Contacts.
   */
  xeroName: string;
  /**
   * The path segment in Xero's own web app, for the "Open in Xero" link.
   * Null where Xero has no addressable page for the object.
   */
  xeroAppPath: string | null;
  /**
   * What this app may do with the entity today. See {@link XeroCapabilities} —
   * and read the per-entity comments in {@link XERO_ENTITIES}, because a
   * `false` here means a different thing on every entity.
   */
  capabilities: XeroCapabilities;
}

/**
 * The three things a Xero integration can offer a record, kept apart because
 * **they are three different backend routes and the answers genuinely differ**.
 *
 * This was one `pushable` boolean until quotes went live, which is the first
 * entity where the three disagree: a quote can be CREATED in Xero, cannot be
 * SYNCED, and cannot be LINKED. One flag could only ever have answered all
 * three the same way, so it would have had to be `true` (offering two buttons
 * that cannot work) or `false` (hiding the one that does).
 *
 * - `create` — `POST /xero/create/{entity}/{id}` makes a NEW Xero record and
 *   links it. The only route that can produce a Xero id from nothing.
 * - `sync`   — `POST /xero/sync/{entity}/{id}` queues an update of a record
 *   that ALREADY has a Xero id. On a record with no id it answers 200 and does
 *   nothing, which is why {@link canCreateInXero} keys on the id, not the
 *   status.
 * - `link`   — attach an existing Xero record a person picked. Needs a search
 *   endpoint to pick FROM, and `GET /xero/search/contacts` is the only one
 *   there is.
 *
 * **A `false` here is never self-explaining, so never read one without its
 * comment.** Two kinds live in this registry, and they call for different
 * sentences on screen:
 *
 * 1. **Structural** — it can never be true, because Xero's data model has no
 *    place for it. Contacts, all three. Saying "not yet" about one of these
 *    tells someone to wait for something nobody will ever build.
 * 2. **Deliberate design** — it could be built and was decided against.
 *    `quotes.sync` and `invoices.sync`, for the identical reason: pushing is
 *    inline on the create route, so `/xero/sync` only ever answers
 *    `XERO_PUSH_IS_INLINE`. Not a gap; a choice, with a reason, and nobody is
 *    going to switch it on.
 */
export interface XeroCapabilities {
  /** May create a NEW counterpart in Xero from this record. */
  create: boolean;
  /** May queue an update of an EXISTING Xero counterpart. */
  sync: boolean;
  /** May attach an existing Xero record chosen by a person. */
  link: boolean;
}

export const XERO_ENTITIES: Readonly<Record<XeroEntity, XeroEntityMeta>> = {
  companies: {
    slug: 'companies',
    label: 'company',
    plural: 'Companies',
    xeroName: 'Contact',
    xeroAppPath: 'Contacts/View',
    /*
     * The complete case, and the only one. A company maps to a Xero Contact
     * one-to-one with a real GUID, the push worker accepts company jobs, and
     * `GET /xero/search/contacts` gives the picker something to pick from.
     */
    capabilities: { create: true, sync: true, link: true },
  },
  contacts: {
    slug: 'contacts',
    label: 'contact',
    plural: 'Contacts',
    xeroName: 'Contact person',
    // A ContactPerson has no page of its own; it is edited inside its company.
    xeroAppPath: null,
    /*
     * STRUCTURAL, and permanent. Not a gap, and nothing to wait for.
     *
     * A Xero ContactPerson has no id of its own and never will — the array is
     * replaced wholesale as part of its parent Contact. With no id there is
     * nothing to create separately, nothing to address for an update, and
     * nothing to link to. {@link CONTACTS_ARE_NOT_XERO_CONTACTS} is the long
     * form, and it is what the UI says here instead of "not available yet".
     */
    capabilities: { create: false, sync: false, link: false },
  },
  quotes: {
    slug: 'quotes',
    label: 'quote',
    plural: 'Quotes',
    xeroName: 'Quote',
    xeroAppPath: 'Quotes/View',
    /*
     * The entity that forced this object apart, so read all three separately:
     *
     * - `create: true` — LIVE. `POST /xero/create/quotes/{id}` builds the Xero
     *   quote from ours and links it. It refuses in many named ways, which is
     *   the entire reason {@link XERO_QUOTE_REFUSALS} exists.
     * - `sync: false` — DELIBERATE BACKEND DESIGN, not a missing feature. The
     *   push worker refuses quote jobs outright. A quote is created inline, in
     *   the request, and from then on it is Xero's document, moving through
     *   Xero's own SENT / ACCEPTED / INVOICED lifecycle. Re-pushing ours over
     *   it is exactly the overwrite `XERO_QUOTE_NOT_DRAFT` exists to refuse.
     *   Nobody is going to switch this on, so never render it as "coming
     *   later".
     *
     *   ⚠️ `POST /xero/sync/quotes/{id}` answers **409 `XERO_PUSH_IS_INLINE`**,
     *   not a silent no-op — so this `false` means the affordance should be
     *   ABSENT, and if one ever renders anyway it fails loudly rather than
     *   leaving a link stuck at `pending` forever.
     * - `link: true` — the endpoint WORKS. `POST /xero/link/quotes/{id}` with
     *   `{ xeroId }` writes the row; `linkEntity` is generic and quotes being
     *   in the registry was all it needed. **But there is no quote search** —
     *   `GET /xero/search/contacts` is contacts-only and has no equivalent, so
     *   the GUID has to be read out of Xero by a person. That is a missing
     *   PICKER, not a missing capability, and the two must not be conflated:
     *   `XeroPanel` already requires an `onRequestLink` handler before it
     *   offers the button, so a page that has no way to obtain a GUID simply
     *   does not pass one. Modelling this as `false` would have been the
     *   comfortable lie — it would have hidden the only recovery path a
     *   mis-pushed quote has.
     *
     * That recovery path is worth stating precisely, because "unrecoverable"
     * is wrong in a way that matters: **the LINK is recoverable** — unlink,
     * then re-link or create afresh — while **the Xero DOCUMENT is not**. A
     * wrong quote created in Xero can only be deleted in Xero, by a human.
     * The recovery action exists; it lives in the other system.
     */
    capabilities: { create: true, sync: false, link: true },
  },
  invoices: {
    slug: 'invoices',
    label: 'invoice',
    plural: 'Invoices',
    xeroName: 'Invoice',
    xeroAppPath: 'AccountsReceivable/View',
    /*
     * Same shape as quotes, for the same reasons: `create` and `link` are live
     * (`POST /xero/create/invoices/{id}`, `POST /xero/link/invoices/{id}`).
     *
     * ⚠️ **`sync` stays `false` permanently, not because it is unbuilt.**
     * Pushing is inline on the create route (`existingXeroId ? update :
     * create`), so `/xero/sync` only ever answers `409 XERO_PUSH_IS_INLINE`.
     * `INLINE_PUSH_REASONS` in the backend's `xero-sync.service.ts` keeps the
     * worker off invoices for the same reason, so a link never sticks at
     * `pending` waiting on a queue that will never drain it.
     *
     * A create pushes a DRAFT — `Status: 'DRAFT'` is a literal in
     * `xero-invoice.payload.ts` and is stripped on update, so an edit can never
     * change it — and the backend refuses a non-draft or paid invoice outright.
     * So nothing this UI offers can issue or alter a real invoice.
     *
     * ⚠️ **The quote-acceptance hook is NOT evidence for these booleans.**
     * Accepting a quote raises an invoice and pushes it to Xero through that
     * hook, which is a different route with a different guard from
     * `POST /xero/create/invoices/{id}` — it proves an invoice can reach Xero,
     * nothing about whether a button here can reach that endpoint.
     */
    capabilities: { create: true, sync: false, link: true },
  },
};

/**
 * Why all three of `contacts.capabilities` are false, in one place so it is not
 * rediscovered — and why this one is permanent rather than pending.
 *
 * **Xero has no concept of a standalone person.** Its `Contact` IS the
 * organisation — that is what an invoice is billed to. People are
 * `ContactPersons[]`, an array nested inside that organisation, and each entry
 * is `{FirstName, LastName, EmailAddress, IncludeInEmails}` and **nothing
 * else**. In particular:
 *
 * 1. A ContactPerson **has no id of its own**. Xero issues no GUID for it, so
 *    there is no "their Xero id" to store on our contact and no way to address
 *    one for an update. The array is replaced wholesale.
 * 2. A contact with no `companyId` has **nowhere to go** in Xero.
 * 3. Only four fields survive the trip. Title, department, mobile, LinkedIn,
 *    date of birth, owner, lead source — none of it exists on the Xero side.
 *
 * So EMERGE Company → Xero Contact is a real one-to-one link with a real id,
 * and EMERGE Contact → a row in that company's ContactPersons array is a
 * projection, not a link. The UI says exactly that rather than showing an id
 * field that can never be filled.
 */
export const CONTACTS_ARE_NOT_XERO_CONTACTS =
  'Xero has no record for a person on their own. A Xero contact is the ' +
  'organisation, and people are a small list nested inside it — first name, ' +
  'last name, email, and whether they receive emails. Xero issues no id for ' +
  'them, so a contact has no Xero id to show. Contacts on a linked company ' +
  'are sent as that list.';

/* -------------------------------------------------------------------------
 * Presentation
 * ---------------------------------------------------------------------- */

export type XeroTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info';

interface StatusPresentation {
  label: string;
  tone: XeroTone;
  /** One sentence an operator can act on. Never restates the label. */
  detail: string;
}

const PRESENTATION: Readonly<Record<XeroSyncStatus, StatusPresentation>> = {
  unlinked: {
    label: 'Not in Xero',
    tone: 'neutral',
    detail: 'No counterpart in Xero yet. Nothing has failed.',
  },
  /*
   * ⚠️ **"This usually clears within a minute" was TRUE OF ONE ENTITY and read
   * as a fact about all of them.** Only companies go through the background
   * worker. The worker refuses quote and invoice jobs outright — they are
   * pushed inline, which is why `/xero/sync` answers `XERO_PUSH_IS_INLINE` for
   * both — so a quote or invoice link sitting at `pending` is not waiting for
   * anything and never clears. The old sentence told a reader to come back in a
   * minute, for ever.
   *
   * The detail is entity-agnostic because {@link statusDetail} is, so it names
   * which entity is queued rather than asserting a wait that is wrong for two
   * of the three. It does not promise a duration for the quote/invoice case
   * because there is none to promise.
   */
  pending: {
    label: 'Queued',
    tone: 'info',
    detail:
      'Waiting on the background worker, which sends companies and usually clears within a ' +
      'minute. Quotes and invoices are not sent by it, so one sitting here is not queued for ' +
      'anything.',
  },
  /*
   * ⚠️ **The label was `Synced`, and the present tense was a claim nothing
   * keeps.** It reads as "kept in step", which is true of no entity here:
   * companies are pushed on a queue that a `conflict` or `archived` link
   * silently stops, and a quote or invoice is written by an inline push that
   * Xero refuses once its copy leaves DRAFT. Nothing on either side reconciles
   * afterwards.
   *
   * `In Xero` states the fact and claims nothing about what happens next. The
   * detail is the part that dates the claim — "the last send" — and it is
   * unchanged because it was already the honest half.
   *
   * ⚠️ `IntegrationsPage` renders its own literal `Synced` on the summary
   * counts and does not read this label. That file is not this one's to edit;
   * the two now use different words for one status.
   */
  synced: {
    label: 'In Xero',
    tone: 'success',
    detail: 'Linked to Xero, and the last send was accepted.',
  },
  failed: {
    label: 'Failed',
    tone: 'danger',
    detail: 'Xero refused the last send. Nothing was changed there.',
  },
  conflict: {
    label: 'Changed in Xero',
    tone: 'warning',
    detail: 'Both sides changed. Choose which version to keep — nothing is overwritten until you do.',
  },
  archived: {
    label: 'Archived in Xero',
    tone: 'warning',
    detail: 'The Xero record is archived, so it cannot be written to. The link is kept so it is not duplicated.',
  },
};

export function statusLabel(status: XeroSyncStatus): string {
  return PRESENTATION[status].label;
}

export function statusTone(status: XeroSyncStatus): XeroTone {
  return PRESENTATION[status].tone;
}

export function statusDetail(status: XeroSyncStatus): string {
  return PRESENTATION[status].detail;
}

/**
 * Sort order for a "needs attention" queue — lowest first.
 *
 * Written out as a lookup rather than inferred from the order of the union,
 * the object literal, or (on the server) the position of an ENUM member. All
 * three of those work right up until someone inserts a status in the middle,
 * at which point every queue in the app silently reorders and nothing errors.
 * The backend reached the same conclusion the hard way: `d_users.role` still
 * carries a deprecated member kept alive purely so it could sit last and not
 * disturb the sort.
 *
 * It is also a **product judgement, not a data property** — "what should
 * someone look at first" is a claim about how people work, and it belongs
 * somewhere a person can read and disagree with.
 *
 * `conflict` leads because it is the only state that can silently lose a
 * figure an accountant entered. `unlinked` is last because it is the resting
 * state of most records and is not a problem at all.
 */
const STATUS_PRIORITY: Readonly<Record<XeroSyncStatus, number>> = {
  conflict: 0,
  failed: 1,
  archived: 2,
  pending: 3,
  synced: 4,
  unlinked: 5,
};

export function statusPriority(status: XeroSyncStatus): number {
  return STATUS_PRIORITY[status];
}

/**
 * Comparator for any list of linked records, most-urgent first.
 *
 * Deliberately does not trust the order the API returns. If the two sides ever
 * disagree about priority that is a disagreement worth seeing, not one to
 * inherit silently from whichever `ORDER BY` happened to run.
 */
export function compareByAttention(a: MaybeLinked, b: MaybeLinked): number {
  return statusPriority(linkOf(a).status) - statusPriority(linkOf(b).status);
}
/**
 * Whether the record has quietly stopped syncing.
 *
 * `conflict` and `archived` are both SKIPPED by the push queue, so further
 * edits in the CRM queue nothing and change nothing. That is correct — neither
 * should be written to — but it is **invisible**: the record keeps saving
 * normally and simply stops reaching Xero.
 *
 * "There is a conflict" and "your edits are no longer reaching Xero" are very
 * different sentences to a salesperson, and only the second one makes them act.
 * Anywhere either state is shown, this is what decides whether to say so.
 */
export function isSilentlyNotSyncing(link: XeroLink): boolean {
  return link.status === 'conflict' || link.status === 'archived';
}

/**
 * The codes that park a link at `archived` because the Xero counterpart was
 * **DELETED there**, not archived there.
 *
 * One per pushable entity, and named rather than pattern-matched on `_GONE`: a
 * suffix test would quietly adopt any future code ending the same way, which is
 * the kind of match that is right until it is silently wrong.
 *
 * ⚠️ **"One per pushable entity" IS THE PREMISE THAT DRIFTS, AND IT DID** — a
 * FOURTH pushable entity arrived with credit notes and this set stayed at three
 * until 2026-09-18. The membership is right; what goes stale is the count of
 * entities it is measured against, and nothing announces a new one. **Re-derive
 * it by grepping the backend for the sites that write `status: 'archived'` with
 * an `errorCode`, and diff those codes against this set** — do not re-read this
 * sentence and count the entities you can remember.
 */
const STOOD_DOWN_AS_GONE: ReadonlySet<string> = new Set([
  'XERO_QUOTE_GONE',
  'XERO_INVOICE_GONE',
  'XERO_CONTACT_GONE',
  /*
   * The fourth, added 2026-09-18. `xero-credit-note.service.ts:907-913` parks
   * the link at `archived` with this code and the message "The link was archived
   * rather than re-created" — the same stood-down-as-gone shape as the three
   * above, verified at that site rather than inferred from the name.
   *
   * ⚠️ **It is NOT reachable from a record page yet**, because a credit note
   * carries no `xero` envelope on the wire (see `src/lib/credit-notes.ts`). It
   * is here so that the sentence is right the day it lands: without it the link
   * falls to the generic `archived` copy and tells somebody to go and look in
   * Xero for a document that was deleted there.
   */
  'XERO_CREDIT_NOTE_GONE',
  /*
   * `ARCHIVED_IN_XERO` — a code whose name said "archived" and whose behaviour
   * meant "deleted" — no longer exists in the backend's source; the one row
   * that carried it now reports `XERO_QUOTE_GONE`. Nothing writes it, so do not
   * re-add it defensively.
   */
]);

/**
 * Whether an `archived` link is archived in Xero, or standing over a document
 * that has been **deleted** there.
 *
 * Both are `archived` and both mean "do not write to this", so nothing about
 * the actions changes — see {@link XeroSyncStatus}. What changes is the
 * sentence and the recovery: an archived counterpart can be un-archived in Xero
 * and the link is still correct, while a deleted one is gone for good and the
 * recovery is to **unlink here, then create afresh** — which is a real recovery
 * path, in this app, and the copy has to say so rather than leaving a dead GUID
 * on screen with no explanation.
 *
 * Note the link still HAS that GUID: the backend's `updateStatus` writes the
 * status and never clears `xero_id`. So `isLinked` stays true, Create is
 * correctly not offered (there is a link to detach first), and Unlink is the
 * button that matters.
 *
 * ⚠️ **A `null` `code` answers `false` here, and that is the designed third
 * branch rather than a gap.** `XeroLinkError.code` is nullable on the wire, so
 * an `archived` link can carry a refusal with no code at all. It is not
 * `XERO_ARCHIVED` either, so {@link isRestorableInXero} is false as well — and
 * the panel then shows the state with **no recovery instruction**, which is
 * exactly what an unrecognised code already gets. That is the honest answer: the
 * two recoveries point at opposite actions (go and un-archive it in Xero / it is
 * destroyed, unlink and create afresh), and guessing between them sends somebody
 * hunting a document that is not there, or worse, tells them a live document is
 * gone. `isGoneInXeroCode` is deliberately NOT widened to take `string | null`,
 * so the null is decided here, once, rather than absorbed silently at the other
 * end where a live `ApiError.code` is asked the same question.
 */
export function isStoodDownAsGone(link: XeroLink): boolean {
  if (link.status !== 'archived') return false;
  const code = link.error?.code;
  return code != null && isGoneInXeroCode(code);
}

/**
 * The same question asked of a bare CODE rather than of a link.
 *
 * A refusal arrives before the link this app holds has been re-read, so at the
 * moment a push is refused there is no updated link to ask — only
 * `ApiError.code`. This is that one set, consulted from the other end, so a
 * caller reading a live refusal and a caller reading a stored link cannot
 * disagree about what "gone" means.
 *
 * ⚠️ **Deliberately NOT a second set, and not a `_GONE` suffix test.** Both are
 * the mistake {@link STOOD_DOWN_AS_GONE} already documents: a second copy
 * agrees only until one is edited, and a suffix match quietly adopts any future
 * code spelled the same way.
 */
export function isGoneInXeroCode(code: string): boolean {
  return STOOD_DOWN_AS_GONE.has(code);
}

/**
 * The refusals that are the ORDINARY OUTCOME of a routine push, not a fault.
 *
 * ⚠️ **This distinction is about TONE, and getting it wrong is expensive in a
 * way nothing measures.** Every other refusal in {@link XERO_QUOTE_REFUSALS}
 * describes something someone has to go and do. This one describes the system
 * working exactly as designed, and it will be the **common** answer once quotes
 * are pushed on every save: the whole intent is that a person sends the quote
 * from inside Xero, and from the moment they do, Xero's copy has left DRAFT and
 * every later push is refused. Rendering that as a failure banner on every save
 * teaches people that the Xero notice means nothing — after which the refusals
 * that DO need acting on are ignored too.
 *
 * So it is data rather than an inline `code === 'XERO_QUOTE_NOT_DRAFT'` at the
 * one place that happens to render it: the set is the claim, and a second code
 * with the same character joins it here rather than being decided again.
 *
 * ⚠️ **`XERO_INVOICE_NOT_DRAFT` is that second code.** It is the identical
 * claim one document along — the backend raises it from `assertStillWritable`
 * (`xero-invoice.service.ts:644-649`) once Xero's copy has left DRAFT, and it
 * will be the COMMON answer on an invoice for the same reason it is on a
 * quote: an invoice is approved and sent from inside Xero, and every push
 * after that is refused. Both pages must describe this one behaviour in the
 * same tone, which is exactly the divergence this set exists to prevent.
 *
 * ⚠️ **`XERO_INVOICE_HAS_PAYMENTS` is deliberately NOT here**, and the backend's
 * own comment above those two throws is the argument: they carry different codes
 * *because they send a person to different screens*. A non-draft invoice can be
 * voided and re-raised; a part-paid one is a reconciliation matter — a credit
 * note or a refund — and money has moved. That is a thing to act on, not a thing
 * to note, so it stays a terminal refusal wearing its own sentence.
 *
 * ⚠️ **`XERO_QUOTE_GONE` is NOT in this set.** It is also terminal and also
 * unfixable from here, but it means a real document was destroyed in the
 * accounting system, and the link that still holds its id needs standing down —
 * which is a thing to look at, not a thing to note. It routes through
 * {@link isGoneInXeroCode} instead.
 */
const SETTLED_IN_XERO: ReadonlySet<string> = new Set([
  'XERO_QUOTE_NOT_DRAFT',
  'XERO_INVOICE_NOT_DRAFT',
  /*
   * The third, added 2026-09-18, and it joins on the character this set names
   * rather than on the shape of its slug. The backend classifies it exactly the
   * same way: `xero-credit-note.service.ts`'s `ACTED_ON_IN_XERO` map — its own
   * "nothing was wrong, report this as `skipped`" list — holds this code, and
   * that map is the server-side twin of this set.
   *
   * ⛔ **`XERO_CREDIT_NOTE_ALLOCATED` IS THE OTHER MEMBER OF THAT BACKEND MAP
   * AND IS DELIBERATELY NOT HERE**, on the `XERO_INVOICE_HAS_PAYMENTS`
   * precedent three paragraphs up: its message is "Un-apply it in Xero first",
   * so credit has been APPLIED against an invoice and money has moved. That is
   * a thing to act on, not a thing to note. **The two maps agree on tone for
   * autopush and must not be assumed to agree on tone for a REFUSAL BANNER** —
   * they answer different questions, and copying the backend's membership
   * wholesale is how the acted-on case gets silently downgraded.
   *
   * ⚠️ Not reachable from a record page yet — a credit note carries no `xero`
   * envelope on the wire.
   */
  'XERO_CREDIT_NOTE_NOT_DRAFT',
]);

/**
 * Whether a refusal code means "Xero has moved on and that is fine".
 *
 * See {@link SETTLED_IN_XERO}. An unknown code is `false` — the safe direction,
 * because the cost of toning a genuine problem neutrally is that nobody acts on
 * it, while the cost of toning this one as a problem is only noise.
 */
export function isSettledInXeroCode(code: string): boolean {
  return SETTLED_IN_XERO.has(code);
}

/**
 * The one `archived` case that is genuinely **restorable**: a Xero contact
 * archived in Xero, whose id is still correct and which a person can
 * un-archive there.
 *
 * ⚠️ **This is the ONLY code that may be told to un-archive.** Pointing the
 * user at un-archiving in Xero is true here and actively wrong for all three
 * `*_GONE` codes, where there is nothing to un-archive.
 *
 * The panel has three branches, not two, and the third is the important one: a
 * code we do NOT recognise gets the state ("do not write to this") and **no
 * recovery instruction at all** — never a silent nothing, and never a recovery
 * guessed from the code's name. Unrecognised means "newer than this map", so
 * the server's own message is what the reader gets. The retired
 * `ARCHIVED_IN_XERO` — whose name said archived and whose behaviour meant
 * deleted — is the evidence that unrecognised is a state worth designing for
 * rather than a case that will not arise.
 */
export function isRestorableInXero(link: XeroLink): boolean {
  return link.status === 'archived' && link.error?.code === 'XERO_ARCHIVED';
}

/** Only a refusal is worth retrying. Re-queueing a `pending` push duplicates it. */
export function canRetry(link: XeroLink): boolean {
  return link.status === 'failed';
}

/**
 * Whether a record needs CREATING in Xero rather than pushing to it.
 *
 * **The discriminator is the id, not the status.** `POST /xero/sync` queues an
 * update of an existing Xero contact; with no `xero_id` there is nothing to
 * update, so the server answers **200 with the envelope unchanged** and nothing
 * happens. That is what "Send to Xero does nothing" was: a success response for
 * a no-op, which is worse than an error because the UI has nothing to report.
 *
 * A `failed` link can sit on either side of this — a failed CREATE has no id, a
 * failed UPDATE has one — so keying on the status alone gets it wrong exactly
 * when it matters. Ask whether there is an id.
 */
export function canCreateInXero(entity: XeroEntity, link: XeroLink): boolean {
  if (!XERO_ENTITIES[entity].capabilities.create) return false;
  if (link.id !== null) return false;
  return link.status === 'unlinked' || link.status === 'failed';
}

/**
 * Whether the record can be **UPDATED** in Xero — a different question from
 * {@link canCreateInXero}, answered by the same endpoint.
 *
 * ⚠️ **`POST /xero/create/{entity}/{id}` is create-OR-update, and this app read
 * it as create-only for as long as it has existed.** The backend branches on
 * one line — `existingXeroId ? updateQuote(…) : createQuote(…)` — and their own
 * file header says it outright: *a quote is created once, then updated*. Because
 * `canCreateInXero` answers false the moment a link has an id, and `canPush` is
 * false because `capabilities.sync` is false, a LINKED quote had no push
 * affordance at all and the working update path was unreachable from this app.
 * The owner hit it for real: a customer PO number typed onto a quote after it
 * had been pushed could never reach Xero.
 *
 * ⚠️ **This is a NEW function rather than a loosened `canCreateInXero`, and
 * that is the whole point.** That guard's job is preventing a SECOND document
 * inside the accounting system — `link.id !== null` means one already exists —
 * and it is exactly right for that job. Widening it in place would have kept
 * one name over two opposite meanings, and the create button would silently
 * have started updating. Two questions, two functions, and
 * {@link pushIntentFor} is the only thing that answers both at once.
 *
 * The statuses excluded, and why each:
 *
 * - **`conflict`** — somebody changed Xero's copy. Overwriting it silently is
 *   the one unrecoverable mistake this integration can make, which is what the
 *   two "keep which version" buttons exist to ask. An automatic push must never
 *   pre-empt that question.
 * - **`archived`** — Xero refuses writes to an archived record, and where the
 *   link was stood down as gone (see {@link isGoneInXeroCode}) there is no
 *   document left to write to at all.
 * - **`unlinked`** — no id, so this is a create, and a create is never
 *   automatic. See {@link pushIntentFor}.
 *
 * `pending` is allowed: for a quote or an invoice that status is not a queue
 * position — nothing is coming to clear it — so refusing an update there would
 * strand the record on a state that never resolves.
 */
export function canUpdateInXero(entity: XeroEntity, link: XeroLink): boolean {
  /*
   * `create`, not `sync`, and deliberately so: the capability names the ROUTE,
   * and the update lives on the create route. `sync` is the worker route, which
   * refuses quotes and invoices with `XERO_PUSH_IS_INLINE` — gating on it here
   * would reinstate exactly the unreachable-update bug this function fixes.
   */
  if (!XERO_ENTITIES[entity].capabilities.create) return false;
  if (link.id === null) return false;
  return link.status === 'synced' || link.status === 'failed' || link.status === 'pending';
}

/**
 * A branded token saying which of the two things a push to
 * `POST /xero/create/{entity}/{id}` will actually do.
 *
 * ⚠️ **The brand is the mechanism, not decoration.** The endpoint decides
 * create-vs-update from the link, so the call site's *belief* about which it is
 * getting has never been checked against anything. A plain
 * `'create' | 'update'` parameter would let any call site assert either, and
 * the endpoint would quietly do the other one — which is precisely the failure
 * this whole change is repairing, arriving from the opposite direction. The
 * symbol makes {@link pushIntentFor} the only producer in the app, so an intent
 * cannot be written down by hand: it can only be **derived from the link**,
 * which is the same thing the server derives it from.
 *
 * ⚠️ **What the brand does NOT stop.** `pushToXero(e, id, { action: 'create' })`
 * and `pushToXero(e, id, 'create')` are both **rejected** by `tsc` — the first
 * naming the missing symbol — so no ordinary call site can express an intent.
 * But `{ action: 'create' } as XeroPushIntent` **compiles**, because the object
 * is a subset of the target and TypeScript allows an assertion between
 * overlapping types. That is the ceiling of every brand in this language: what
 * it buys is that forging an intent has to be WRITTEN AS A CAST, visible in a
 * diff, rather than happening by passing a plausible string.
 */
declare const XERO_PUSH_INTENT: unique symbol;

export interface XeroPushIntent {
  /** What the endpoint will do, for copy and for reporting. Never for deciding. */
  readonly action: 'create' | 'update';
  readonly [XERO_PUSH_INTENT]: true;
}

/**
 * What a push to this record would do — or `null` where it may not be pushed
 * at all.
 *
 * **The single decision point.** A caller does not pick an intent; it asks what
 * the link supports and is told. `'create'` is offered only where
 * {@link canCreateInXero} agrees (so the duplicate guard is unchanged and still
 * runs), `'update'` only where {@link canUpdateInXero} agrees, and the two are
 * mutually exclusive by construction because one requires `link.id === null`
 * and the other requires the opposite.
 *
 * ⚠️ **An automatic caller must check `action` and refuse `'create'` — EXCEPT
 * on the create forms.** Nothing here can enforce either half, because the same
 * function serves the deliberate button and the forms alike.
 *
 * The rule: writing a NEW document into SEED's accounting system is not
 * something a save may do on someone's behalf, and creating stays a button a
 * person presses. That holds everywhere it is aimed: a background save, a
 * retry, an inline field edit, anything a person did not sit down and ask for.
 *
 * The one exception is `QuoteFormModal` and `InvoiceFormModal`: submitting one
 * of those forms IS the person pressing the button — they filled the form in
 * and pressed Create — so requiring a second press on the record afterwards is
 * not consent, it is a step people forget, leaving documents that never reach
 * Xero and are found at reconciliation.
 *
 * ⚠️ **Do not generalise this to "automatic creates are fine now."** Two
 * properties make it safe HERE and neither is free:
 *
 * - **A cooldown is checked first and the call is skipped, not queued.** See
 *   `xero-cooldown.ts`. Nothing in this app retries a Xero write on a timer.
 * - **A retry after an ambiguous failure cannot duplicate.** The server derives
 *   create-vs-update from its OWN link, so if the first call landed and linked,
 *   the retry updates. This matters more than it looks: Xero does not enforce
 *   invoice-number uniqueness — INV-0881 exists twice in this tenant, once paid
 *   and once deleted — so a genuine duplicate would be two live demands for
 *   money with nothing in the accounting system refusing them.
 *
 * A new automatic caller must re-derive both, not inherit them by citing this
 * paragraph.
 */
export function pushIntentFor(entity: XeroEntity, link: XeroLink): XeroPushIntent | null {
  if (canCreateInXero(entity, link)) return { action: 'create' } as XeroPushIntent;
  if (canUpdateInXero(entity, link)) return { action: 'update' } as XeroPushIntent;
  return null;
}

/**
 * Whether "Send to Xero" should be offered at all.
 *
 * `archived` is excluded deliberately — Xero rejects writes to an archived
 * contact, so the button would exist only to produce an error.
 *
 * Requires an id: see {@link canCreateInXero} for why a missing one routes to
 * create instead, and why status alone cannot decide it.
 */
export function canPush(entity: XeroEntity, link: XeroLink): boolean {
  if (!XERO_ENTITIES[entity].capabilities.sync) return false;
  if (link.id === null) return false;
  return link.status === 'failed';
}

/** Linking is how an operator resolves both "never linked" and a bad guess. */
export function canLink(entity: XeroEntity, link: XeroLink): boolean {
  if (!XERO_ENTITIES[entity].capabilities.link) return false;
  return link.status === 'unlinked' || link.status === 'failed';
}

export function isLinked(link: XeroLink): boolean {
  return link.id !== null;
}

/**
 * Whether Xero can do ANYTHING with this entity — the third question, and the
 * one {@link XeroCapabilities} does not answer with a single field.
 *
 * It exists because the alternative is a call site picking whichever of the
 * three booleans happens to read true today, which is how the split gets
 * quietly undone. "Is this wired to Xero at all" is a real question — the
 * integrations page asks it to name the entities that are not — and it is not
 * `create`, even though `create` is true for every entity that is wired right
 * now. That coincidence is exactly the trap: the day an entity arrives that can
 * only be LINKED, a `create` test would call it unwired and hide it.
 *
 * **Not a substitute for the specific ones.** Gating a button on this offers an
 * action the entity may not support: quotes are wired, and pushing one is still
 * refused. Use it only for the "does this exist for Xero" sentence, never for
 * "may I press this".
 */
export function isWiredToXero(entity: XeroEntity): boolean {
  const { create, sync, link } = XERO_ENTITIES[entity].capabilities;
  return create || sync || link;
}

/* -------------------------------------------------------------------------
 * How `POST /xero/create/{entity}/{id}` refuses
 *
 * Two layers, because the endpoint serves two entities with different failure
 * surfaces. {@link XeroCreateConflict} covers the 409s a COMPANY create can
 * produce; {@link XERO_QUOTE_REFUSALS} covers the coded refusals a QUOTE
 * create can produce. Both switch on `ApiError.code` and neither reads message
 * text — production flattens messages and the wording belongs to the server.
 *
 * ⚠️ **No count of entries is stated here on purpose** — a number in a comment
 * has drifted wrong before and cannot be wrong loudly. Anything that needs the
 * number should read `XERO_QUOTE_REFUSALS.size`, which cannot go stale.
 * ---------------------------------------------------------------------- */

/**
 * Which 409 came back from a create, in terms of what RESOLVES it.
 *
 * Both are terminal — **neither is ever retryable** — but they are resolved by
 * opposite actions, and offering the wrong one is worse than offering nothing:
 *
 * - `already-linked` — the record already points at a Xero contact, so there is
 *   nothing to pick and the picker would be a dead end. The only move is to
 *   detach the link that exists (or to accept it and stop). The server's
 *   message names the existing GUID, which is why it is shown verbatim.
 * - `name-taken` — a Xero contact with this name already exists, so the answer
 *   is to LINK to it. A retry can only fail the same way, because a taken name
 *   never comes free; and a user who "resolves" it by editing the name has
 *   created a duplicate customer inside the accounting system that nothing on
 *   our side can detect or undo. That is the one mistake this integration must
 *   not invite.
 * - `other` — any 409 code this app does not recognise. Degrades to the safe
 *   behaviour: show the server's message, offer the picker, offer no retry.
 */
export type XeroCreateConflict = 'already-linked' | 'name-taken' | 'other';

/**
 * A `Map`, not an object literal, so `createConflictOf('toString')` cannot
 * inherit a match off `Object.prototype` — the same care `isXeroEntity` takes.
 */
const CREATE_CONFLICT_CODES: ReadonlyMap<string, XeroCreateConflict> = new Map([
  ['XERO_ALREADY_LINKED', 'already-linked'],
  ['XERO_CONTACT_NAME_TAKEN', 'name-taken'],
] as const);

/**
 * Classify a create refusal by `ApiError.code`. **Never by message text** — the
 * wording belongs to the server, and production flattens it.
 *
 * An unknown code is `'other'` rather than an error: a new code appearing
 * server-side must degrade to the safe branch, not break the panel.
 */
export function createConflictOf(code: string): XeroCreateConflict {
  return CREATE_CONFLICT_CODES.get(code) ?? 'other';
}

/* -------------------------------------------------------------------------
 * How `POST /xero/create/quotes/{id}` refuses
 * ---------------------------------------------------------------------- */

/**
 * WHERE the person has to go to fix a refused quote create.
 *
 * This — not the HTTP status, and not the wording — is the useful axis. The
 * backend issues distinct codes precisely because they send the reader to
 * different places, and a screen that renders them all as "couldn't send to
 * Xero, try again" sends everyone to the same wrong one. Two of these
 * destinations are not even in this app.
 *
 * ⚠️ **No count is stated here on purpose** — read {@link XeroRefusalDestination}
 * for the current set; the type is the only thing that cannot go stale.
 *
 * - `quote-form`     — something is missing on the quote itself. Fixable right
 *                      here: reopen the quote and fill it in.
 * - `company-record` — the quote is fine; the COMPANY it bills is not set up
 *                      for Xero. A DIFFERENT record, on a different page, and
 *                      the fix helps every future quote for that customer.
 *                      Sending someone to the quote form for one of these is
 *                      the most likely wrong turn, because the error arrived
 *                      while they were looking at a quote.
 * - `quote-form-or-company`
 *                    — a DOCUMENT-level Xero setting is missing from **both**
 *                      places that could carry it: the quote's own field and the
 *                      company's Xero Sales default. Either one clears it.
 *                      ⚠️ **Not the same shape as `quote-lines-or-company`,
 *                      and collapsing the two would lose the difference.**
 *                      There a value EXISTS and the wire will not say which
 *                      record supplied it, so neither may be asserted; here
 *                      NEITHER record supplied one, nothing is unknown, and both
 *                      fixes are real. Name both controls in their own words —
 *                      they are different fields, on different screens, and one
 *                      of them clears exactly this document while the other
 *                      clears every future one for that customer.
 * - `quote-lines-or-company`
 *                    — a Xero TAX CODE was refused, and it came from one of two
 *                      records: a line item's own `xeroTaxType` override, or —
 *                      where the line sets none — the company's Xero Sales
 *                      default. This is the one destination that must be able to
 *                      name TWO screens; see the `XERO_TAX_TYPE_NOT_REVENUE`
 *                      entry in {@link XERO_QUOTE_REFUSALS} for the measurement
 *                      that forced it out of `company-record`.
 *
 *                      The wire CAN say which of the two records set the code —
 *                      see {@link refusalOriginOf}. The destination stays fixed
 *                      per CODE and is the CEILING of what it alone may claim;
 *                      the response narrows it further at render time.
 * - `xero-settings`  — nothing in EMERGE is wrong and nothing in EMERGE can
 *                      fix it. The change is inside Xero's own settings.
 *                      **Never send the reader hunting for a field here.**
 * - `nowhere`        — terminal. Xero has moved the document beyond our reach
 *                      and no edit on either side brings it back.
 */
export type XeroRefusalDestination =
  | 'quote-form'
  | 'company-record'
  | 'quote-form-or-company'
  | 'quote-lines-or-company'
  | 'xero-settings'
  | 'nowhere';

/**
 * What one refusal code means, in terms a screen can act on.
 *
 * `retryable` is typed as the literal `false` rather than `boolean`, because
 * **every refusal here is one**: each names a state that a repeat of the exact
 * same request reproduces exactly. A retry button on any of them is a button
 * that spends a Xero API call to show the same message again. Keeping the key
 * (rather than dropping it as always-false) means a caller asks the question
 * and reads the answer, instead of assuming; and the literal type means the day
 * a genuinely retryable code appears, widening it is a deliberate edit here
 * rather than a quiet `true` in the map.
 */
export interface XeroQuoteRefusal {
  destination: XeroRefusalDestination;
  /** Always false — see the interface comment. Repeating the call cannot help. */
  retryable: false;
  /**
   * Whether the SERVER's message carries information no fixed copy of ours can.
   *
   * True wherever the useful half of the sentence is derived PER TENANT and so
   * cannot be written here: which sales rate to pick instead, which tracking
   * option is missing, which theme. Our own explanation is fine for the rest,
   * so `false` does not mean "hide the message" — it means "our sentence is not
   * losing anything by standing in front of it".
   *
   * Grep `verbatim: true` for the current set rather than trusting a count in
   * prose.
   */
  verbatim: boolean;
}

/**
 * Code → refusal. A `Map`, not an object literal, so a code that happens to
 * spell `toString` or `constructor` cannot inherit a match off
 * `Object.prototype` — the same care {@link isXeroEntity} and
 * {@link createConflictOf} take.
 *
 * **Grouped by destination, which is the thing to preserve when editing.** The
 * groups are not cosmetic: each is a different place to send the reader. Read
 * {@link XeroRefusalDestination} for the current set rather than a count in
 * prose, the same rule `verbatim` carries above.
 */
export const XERO_QUOTE_REFUSALS: ReadonlyMap<string, XeroQuoteRefusal> = new Map<
  string,
  XeroQuoteRefusal
>([
  /*
   * 422 → the quote form. Ours to fill in.
   */
  // No company on the quote, so Xero has nobody to bill.
  ['XERO_QUOTE_NO_COMPANY', { destination: 'quote-form', retryable: false, verbatim: false }],
  // No line items. Xero will not take an empty quote.
  ['XERO_QUOTE_NO_LINES', { destination: 'quote-form', retryable: false, verbatim: false }],
  /*
   * ⚠️ **`XERO_QUOTE_NO_PROJECT` no longer exists and never will.** The backend
   * deleted this refusal, and `XERO_INVOICE_NO_PROJECT` with it: a quote or
   * invoice that can derive no tracking now pushes UNTRACKED, because
   * `Tracking` is optional in Xero's API. The former guard demanded a project
   * record most products cannot have (1 of 28 products can carry one), refused
   * documents Xero itself accepts untracked, and its only reachable workaround
   * was attaching an unrelated project — producing confidently wrong tracking,
   * which is worse than visibly absent tracking. Not re-added for that reason.
   *
   * ⚠️ **This does NOT loosen the per-LINE guard.** `project-line.guard.ts` is
   * untouched: a line whose product IS sold through projects and names none is
   * still refused at write time. That is refusal #1 in `src/lib/line-project.ts`
   * (`kind: 'project-required'`), routed by `path` to a control rather than by
   * `code` to a destination.
   */
  /*
   * A line's `taxRate` disagrees with the rate implied by its `xeroTaxType`.
   *
   * Why the refusal exists: the push sends Xero the `TaxType` CODE and nothing
   * else, and Xero works the money out from its OWN rate table — while our
   * `lineTax` is a generated column computed from `taxRate` alone. So the two
   * systems can hold the same agreement at two different totals, with both
   * documents looking entirely correct; only the comparison catches it, which
   * is why it is a refusal before the push rather than a reconciliation after
   * one. The tolerance is one hundredth of a percentage point —
   * `DECIMAL(5,2)`'s own resolution — so there is no fuzzy band to reason about.
   *
   * `quote-form`, and a genuine one rather than a fallback: BOTH remedies the
   * message offers — change the line's tax rate, or choose the Xero tax code
   * that charges that rate — are edits to a LINE ITEM in the quote's own line
   * grid, since the rate and the code being compared both live on that line.
   *
   * `verbatim` because everything actionable is computed per line and per
   * tenant: the product name, both percentages, and both of Xero's own code
   * names (`ZERORATEDOUTPUT` / "Zero Rated Output"). A fixed sentence here
   * could only say that a rate and a code disagree, dropping the two numbers
   * and two names that say which to change.
   *
   * The 422 carries **no `details` array**, deliberately — it matches the other
   * `QuoteNotPushable` refusals in this map, all of which are code + message. So
   * there is nothing to route to a field, and `ApiError.fieldErrors` is empty:
   * this renders as a form-level message, never as a marked control.
   */
  ['XERO_TAX_RATE_MISMATCH', { destination: 'quote-form', retryable: false, verbatim: true }],

  /*
   * 422 → `/companies/{id}`. The customer record is not ready, and the fix,
   * once made, clears every future quote for that company at once.
   *
   * ⚠️ Editing the quote does not fix every entry that once lived under this
   * heading: `XERO_NO_LINE_AMOUNT_TYPE` has since moved to
   * `quote-form-or-company`, and `XERO_NO_TAX_TYPE` below is genuinely
   * company-only only within the imprecision documented at its own entry.
   * Re-check both before adding a fourth member here.
   */
  // The company has no Xero contact, so there is nothing to bill in Xero.
  ['XERO_COMPANY_NOT_LINKED', { destination: 'company-record', retryable: false, verbatim: false }],
  /*
   * No Sales tax default on the company and no tax code on the line.
   *
   * The same both-absent shape as `XERO_NO_LINE_AMOUNT_TYPE` below — the
   * backend's `resolveLineTaxType` is `line.xeroTaxType ?? companyDefault` and
   * refuses only when both are empty, with a message already offering both fixes
   * ("Set this company's Xero Sales defaults, or choose a tax code on the line").
   *
   * ⚠️ **STAYS on `company-record`, but the heading copy is imprecise: a tax
   * code on the LINE also clears it, without touching the company** — reachable
   * today, since company 15 carries `xeroSalesTaxType: null` in production. Not
   * moved to `quote-form-or-company` because the quote-side control is the Tax
   * rate column in the LINE GRID, not the quote form's own Tax field — a shared
   * destination would name the wrong control on one of the two codes. Not
   * `quote-lines-or-company` either, since that copy asserts Xero refused a
   * code and can't say which record supplied it, both false when both places
   * are empty. `XeroPanel`'s `REFUSAL_HEADINGS` / `REFUSAL_PLACES` override the
   * two strings for this code alone rather than adding a further destination.
   */
  ['XERO_NO_TAX_TYPE', { destination: 'company-record', retryable: false, verbatim: false }],
  /*
   * 422 → EITHER this quote's own Tax field OR the company's Sales defaults, and
   * **both are empty**, which is what makes both fixes real.
   *
   * `QuoteFormModal` renders a `Tax` select over `DOCUMENT_LINE_AMOUNT_OPTIONS`
   * bound to `xeroLineAmountType` (and `InvoiceFormModal` the same), so the
   * quote-side fix is a control on screen, not a dead end.
   *
   * What the backend does — `resolveAmountType` in `xero-quote.payload.ts`, and
   * identically in `xero-invoice.payload.ts`:
   *
   * ```ts
   * input.quote.lineAmountType ?? contactAmountTypeToDocument(input.company?.salesLineAmountType)
   * ```
   *
   * ⚠️ **Document-then-company, like the tax code below — but NOT the same
   * ambiguity.** `XERO_TAX_TYPE_NOT_REVENUE` fires on a code that WAS set, with
   * provenance discarded before the check, so neither record may be named. This
   * one fires only when the `??` yields nothing, requiring BOTH sides empty.
   * Nothing is unknown: the server's own message states both facts ("This
   * company has no Xero Sales default … and the quote does not set one"), and
   * either control clears it — the copy names both as working fixes, not
   * suspects. Which to prefer is a real difference the copy states: the
   * company's default clears every future quote for that customer at once, the
   * quote's own field clears exactly this document.
   *
   * ⚠️ **The two controls do not store the same STRING, and no copy may imply
   * they do.** A company takes the CONTACT vocabulary (`NONE`) and a document
   * takes the DOCUMENT vocabulary (`NOTAX`) for the choice a person reads as "No
   * tax" — different words, not different casing. The backend routes every
   * company value through `contactAmountTypeToDocument`, and this app keeps
   * `XeroLineAmountType` and `XeroDocumentLineAmountType` as two types so a
   * confused call site fails at `tsc`. The human-facing labels ARE identical in
   * both pickers ("Tax exclusive" / "Tax inclusive" / "No tax"), so choosing the
   * same option in either place is correct — but never carry a stored value
   * across, and never write that the two share a value.
   */
  [
    'XERO_NO_LINE_AMOUNT_TYPE',
    { destination: 'quote-form-or-company', retryable: false, verbatim: false },
  ],
  /*
   * 422 → EITHER this quote's line items OR the company's Sales defaults. Both
   * codes below are about one value — the Xero tax CODE the push would send —
   * and that value has two possible sources.
   *
   * In the backend's `xero-quote.payload.ts`, `assertTaxTypeUsable` is called on
   * the **already-resolved** code — "resolved by `resolveLineTaxType` — the
   * line's own override where it has one, otherwise the COMPANY's sales
   * default." By the time the check runs, the provenance is gone from the code
   * and the message alike, so naming one record from the code alone is a guess
   * dressed as a diagnosis.
   *
   * The RESOLUTION ORDER is the one static fact the copy can offer: a code set
   * on the line wins, so check the line items first, and the company's Sales
   * default only applies to lines that set none.
   *
   * The backend also ships real provenance on the response: a `source` on the
   * detail entry, with `lineId` and `productName` beside it — see
   * {@link XeroTaxRefusalOrigin} and {@link refusalOriginOf}.
   *
   * ⚠️ **The destination below is deliberately NOT split per source.** `source`
   * arrives on the RESPONSE, per refusal; a destination is a property of the
   * CODE, fixed at build time, and the same code answers `line` on one quote and
   * `company` on the next — no static destination can be right for both.
   * Narrowing happens where the response is in hand: `XeroPanel` resolves
   * heading and paragraph as **source → code → destination**, and the
   * both-places copy below stays as the fallback for three live cases: `source`
   * is `null` on `XERO_NO_TAX_TYPE` (neither record supplied a code, so both
   * fixes are genuinely real), the fields are absent on a server that predates
   * the provenance (a state a rollback can reproduce), and an unrecognised
   * `source` value degrades to the same place.
   */
  /*
   * An EXPENSE rate on a sales document. **The only refusal whose message tells
   * you what to pick**: the server derives the SALES rate at the same
   * percentage from Xero's live tax list and names it. That name cannot be
   * written into this file — it is per tenant, and it changes when the tenant's
   * tax rates change — so the message is shown word for word and our own copy
   * stays out of its way. `verbatim` is load-bearing here for a second reason
   * now: our copy has to name two possible places, so it is doubly the half that
   * must not stand in front of the one sentence that names the exact fix.
   */
  [
    'XERO_TAX_TYPE_NOT_REVENUE',
    { destination: 'quote-lines-or-company', retryable: false, verbatim: true },
  ],
  /*
   * The tax code was renamed or archived inside Xero; pick a live one. Same two
   * possible sources as the entry above — a stale code sits on a line just as
   * easily as on a company default, and this refusal cannot tell them apart
   * either.
   */
  [
    'XERO_TAX_TYPE_UNKNOWN',
    { destination: 'quote-lines-or-company', retryable: false, verbatim: false },
  ],

  /*
   * 422 → Xero's own settings, which are not in this app at all.
   *
   * No Location tracking option in Xero matches a region the quote reports
   * into. `Latin America` is the only value that produces this today — the
   * tenant's tracking category simply has no option for it, and the fix is to
   * add one in Xero. (`MENA` used to be a second one; migration 062 renamed it
   * to Xero's own spelling, `Middle East & North Africa`, and it now matches.)
   * There is no EMERGE field to correct, so pointing anywhere in this app sends
   * the reader looking for something that does not exist, and a retry before
   * someone edits Xero cannot do anything but fail again.
   *
   * ⚠️ **"the quote's region" is no longer the right phrase, and the difference
   * is not cosmetic.** Tracking is derived PER LINE now: each line takes
   * Location and Tour Type from **its own project**, falling back to the
   * document's project only where the line has none, with the document-level
   * `xeroTrackingLocation` / `xeroTrackingTourType` overrides winning over both.
   * A quote spanning two regions therefore reports into TWO Xero buckets, and
   * this refusal can be raised by a line the reader was not thinking about —
   * so never word it as though the document had one region. (The backend was
   * forced the same way: its `tourTypeToCreate` became `tourTypesToCreate:
   * string[]`, because one value would have created the first missing Tour Type
   * and pushed the second line **untracked**, with a `200` and a document that
   * looked right.)
   *
   * **Two fallbacks in that derivation are SILENT and are with the owner, not
   * decided.** Recorded so nobody implements a guess: a line whose project
   * records no region carries **no Location** rather than inheriting the
   * document's, and a document where only SOME lines have projects is **not
   * refused**. Neither produces a code, so neither can be caught here.
   *
   * `verbatim` because the server's message NAMES THE ACTION — which option is
   * missing, and that a person with Xero access has to add it. No static copy
   * here can carry that, for the same reason `XERO_TAX_TYPE_NOT_REVENUE` is
   * verbatim: the useful half of the sentence is derived per tenant.
   *
   * ⚠️ **There is deliberately no Tour Type counterpart, and there cannot be.**
   * `resolveTracking` refuses an unknown Location and *creates* an unknown Tour
   * Type. The asymmetry is the point: Location maps onto SEED's five regions, a
   * closed set an accountant curates, and inventing a region inside someone's
   * chart of accounts is not ours to do — while Tour Type grows every intake,
   * where refusing would mean refusing next year's business by design. Write no
   * Tour Type refusal copy; it cannot fire.
   */
  [
    'XERO_TRACKING_LOCATION_UNKNOWN',
    { destination: 'xero-settings', retryable: false, verbatim: true },
  ],

  /*
   * 409 → nowhere. Terminal, and not fixable by editing anything on either
   * side.
   */
  /*
   * The Xero quote has moved past DRAFT — it is SENT, ACCEPTED or INVOICED. It
   * is never overwritten: past draft the document has been shown to a customer
   * or turned into money, and replacing it silently is the unrecoverable
   * mistake this integration must not make. Not a race, so not a retry.
   */
  ['XERO_QUOTE_NOT_DRAFT', { destination: 'nowhere', retryable: false, verbatim: false }],
  /*
   * The Xero quote was deleted there, and the link has been stood down as a
   * result.
   *
   * ⚠️ **"Stood down" means `archived`, NOT `unlinked`.** The backend sets the
   * link's status to `archived` and its `updateStatus` never clears `xero_id`,
   * so the record keeps a GUID that now addresses nothing. Practically:
   *
   * - `isLinked` stays true, so **Create is correctly NOT offered** — there is
   *   a link to detach first. Promising "a fresh create" as the next step names
   *   a button that is not on screen.
   * - The real sequence is **Unlink here, then Create afresh**, and that is a
   *   recovery path in THIS app. Say where it is rather than calling anything
   *   unrecoverable — the Xero document is the part nobody here can bring back.
   * - The status reads `archived` while nothing in Xero is archived. See
   *   {@link isStoodDownAsGone}, which is what tells the two apart.
   */
  ['XERO_QUOTE_GONE', { destination: 'nowhere', retryable: false, verbatim: false }],
  /*
   * ⚠️ **This one is OUR bug, not the user's, and it should be unreachable.**
   *
   * `POST /xero/sync/quotes/{id}` answers this because a quote is pushed
   * inline, never queued — so seeing it means this app offered a sync
   * affordance on an entity whose `capabilities.sync` is `false`. The user can
   * do nothing about that, which is why the destination is `nowhere`.
   *
   * It is in this map so that if the gating ever regresses, the failure is a
   * named refusal rather than an unrecognised code falling through to a raw
   * server message. Before this code existed, the same call answered **200**
   * and queued a job the worker then skipped, leaving the link at `pending`
   * forever with no way to report the regression at all.
   */
  ['XERO_PUSH_IS_INLINE', { destination: 'nowhere', retryable: false, verbatim: true }],
]);

/*
 * ## What is deliberately NOT in the map, and why each one stays out
 *
 * Verified by reading every code the quote and invoice push paths can raise
 * (`xero-quote.payload.ts`, `xero-quote.service.ts`, `xero-invoice.payload.ts`,
 * `xero-invoice.service.ts`, `xero-links.controller.ts` for the route-level
 * refusal) rather than by reading a table in a doc. Every code either push can
 * raise is either a key above or named in this block. **Count the map; do not
 * trust a number in prose** — the backend's own source disagrees with itself
 * about how many refusals two files that raise identical tax codes have.
 *
 * ### `XERO_UPSTREAM_UNAVAILABLE` (502) — and it is the reason `retryable` is
 * typed as the literal `false`
 *
 * The quote push really can raise it: after creating a missing Tour Type option
 * the payload is rebuilt, and if Xero still does not report the option the
 * backend refuses rather than sending a quote missing its tracking. **That one
 * IS worth retrying**, which is exactly why it must not be in this map: adding
 * it would force `retryable` to widen from the literal `false` to `boolean`,
 * and the whole guarantee — that no member of this map ever gets a retry button
 * — would quietly become a per-entry claim nobody re-checks.
 *
 * It is not a refusal at all. Nothing about the quote is wrong, no screen fixes
 * it, and no destination in the set describes "Xero was unreachable". It
 * falls through {@link quoteRefusalOf} to `null` and renders as the server's own
 * message, which is the correct handling and needs no entry. If a retry
 * affordance is ever wanted for it, build it as a separate transient-failure
 * test — not as a widened field here.
 *
 * ### Connection-level codes — `XERO_NOT_CONNECTED` (409),
 * `XERO_NOT_CONFIGURED` (503), `XERO_REAUTH_REQUIRED` (409),
 * `XERO_RATE_LIMITED`, `XERO_TOKEN_REJECTED`
 *
 * Reachable from a quote push, but they are facts about the CONNECTION, not
 * about the quote — the same push refused for the same reason on every record
 * in the system. {@link XeroConnection} and the panel's disconnected footer
 * already own that sentence, and it points at `/integrations`, which is not one
 * of the destinations at all. Routing them here would put "fix this quote" copy in
 * front of a problem no quote has.
 *
 * ### The invoice codes — `XERO_INVOICE_NO_COMPANY`, `XERO_INVOICE_NO_LINES`,
 * `XERO_INVOICE_NOT_DRAFT`, `XERO_INVOICE_GONE`, `XERO_INVOICE_HAS_PAYMENTS`,
 * `XERO_INVOICE_LINE_NO_ITEM_CODE`
 *
 * `XERO_INVOICE_NO_PROJECT` is gone alongside `XERO_QUOTE_NO_PROJECT` (see the
 * struck-through entry in {@link XERO_QUOTE_REFUSALS}); an invoice that can
 * derive no tracking now pushes untracked.
 *
 * `invoices.capabilities.create` is `true`, so a button in this app DOES reach
 * the invoice push and these codes ARE reachable. They stay out of the map
 * anyway:
 *
 * 1. **Two are already routed elsewhere and would COLLIDE.**
 *    `XERO_INVOICE_NOT_DRAFT` is in {@link SETTLED_IN_XERO} and
 *    `XERO_INVOICE_GONE` in {@link STOOD_DOWN_AS_GONE}; both are deliberately
 *    toned by their caller and never reach `RefusalBanner`. A key here would
 *    give each a second, contradictory rendering.
 * 2. **A noun is not a sentence.** `XERO_QUOTE_NOT_DRAFT`'s copy names Xero's
 *    QUOTE lifecycle — "sent, accepted or invoiced" — which are not an invoice's
 *    statuses. Swapping the noun in would read fluently and be false, worse than
 *    the unrouted server message it would replace.
 * 3. **`XERO_INVOICE_HAS_PAYMENTS` needs its own sentence** — a part-paid
 *    invoice is terminal in a way a draft is not — and
 *    `XERO_INVOICE_LINE_NO_ITEM_CODE` points at a LINE rather than at any
 *    destination in the current set.
 *
 * So: when they come in, they come in together with the copy. The shared codes
 * already in this map (`XERO_COMPANY_NOT_LINKED`, `XERO_NO_TAX_TYPE`,
 * `XERO_TAX_TYPE_NOT_REVENUE`, `XERO_TAX_TYPE_UNKNOWN`,
 * `XERO_NO_LINE_AMOUNT_TYPE`, `XERO_TAX_RATE_MISMATCH`,
 * `XERO_TRACKING_LOCATION_UNKNOWN`) already reach an invoice push, so
 * `XeroPanel.tsx`'s headings, explanations and places copy are parameterised on
 * `XERO_ENTITIES[entity].label` rather than hardcoded to "quote".
 *
 * ### Line-item refusals live somewhere else entirely, and do not overlap
 *
 * `src/lib/line-project.ts` handles the guard that refuses a line whose product
 * is sold through projects and does not name one. Count that model in ITS file,
 * not here.
 *
 * ⚠️ That guard is **NOT affected** by the removal of `XERO_QUOTE_NO_PROJECT` /
 * `XERO_INVOICE_NO_PROJECT`. Those were DOCUMENT-level push refusals about
 * tracking; this is a LINE-level write-time refusal about a product sold
 * through projects, and the backend left it untouched deliberately. "Projects
 * are no longer required" is the wrong lesson to carry from one to the other.
 *
 * ⚠️ **The two models do not share "no `error.code`" as a distinguishing axis**
 * — `PROJECT_HAS_NO_CITIES` and `CITIES_NOT_ON_PROJECT` carry codes too. What
 * keeps them apart is narrower and holds regardless: **neither code is a key in
 * this map**, so {@link quoteRefusalOf} answers `null` for them and the caller
 * falls back, exactly as it should for a refusal belonging to another model.
 * Check that property when adding a key here — a line refusal's code landing in
 * this map would route a form-control problem to a whole screen.
 *
 * The division of labour: line refusals are routed by `path` (`projectId` /
 * `cityIds`) to a control, these by `code` to a destination.
 */

/**
 * Classify a quote-create refusal by `ApiError.code`. **Never by message
 * text** — production flattens messages and the wording belongs to the server.
 *
 * **`null` means "a code this app has not been taught", and the caller MUST
 * fall back to showing the server's own message.** That is not a defensive
 * afterthought: the backend is still adding codes to this endpoint, so an
 * unrecognised one is the expected case for anything shipped after this file
 * was last edited. Inventing a destination for it — defaulting to the quote
 * form because most refusals point there, say — would send someone to edit a
 * quote over a problem in Xero's settings, and they would have no way to tell
 * that the guidance was guessed. A server message with no destination is
 * strictly better than a confident wrong one.
 *
 * Deliberately does NOT check `error.status`. The code is the contract; if the
 * backend ever re-classifies one of these between 409 and 422 the meaning is
 * unchanged and the guidance should not silently disappear.
 *
 * `null` is also the RIGHT answer for several codes this app knows about and
 * keeps out of the map on purpose — the transient upstream failure, the
 * connection-level ones, and the invoice set. See the block under
 * {@link XERO_QUOTE_REFUSALS} for each. So a caller's fallback path is not a
 * rare branch to be tested last: it is the live path for real errors today.
 */
export function quoteRefusalOf(error: ApiError): XeroQuoteRefusal | null {
  return XERO_QUOTE_REFUSALS.get(error.code) ?? null;
}

/* -------------------------------------------------------------------------
 * WHERE the refused tax code came from
 * ---------------------------------------------------------------------- */

/**
 * Which record supplied the Xero tax code a push refused.
 *
 * Deliberately only the two values the server sends. Anything else — a third
 * value from a newer server, a typo, a rollback that sends nothing — reads as
 * `null` in {@link XeroTaxRefusalOrigin}, which is the same "we do not know"
 * the whole feature was built without.
 */
export type XeroTaxSource = 'line' | 'company';

/**
 * The provenance the backend ships on a tax refusal, read off the wire.
 *
 * `source` answers where a tax code came from: a line item's own
 * `xeroTaxType`, or the company's Xero Sales default where the line sets none.
 * See the `quote-lines-or-company` block in {@link XERO_QUOTE_REFUSALS} for
 * why that matters.
 *
 * **The four rules the backend states, coded against rather than inferred:**
 *
 * 1. It rides on `XERO_TAX_TYPE_UNKNOWN`, `XERO_TAX_TYPE_NOT_REVENUE`,
 *    `XERO_NO_TAX_TYPE` and `XERO_TAX_RATE_MISMATCH`, on **both** the quote and
 *    the invoice push.
 * 2. **`source` is `null` on `XERO_NO_TAX_TYPE`** — neither record supplied a
 *    code, so "where did it come from" has no answer, and BOTH fixes are real.
 *    That code must keep the two-places copy.
 * 3. **`lineId` is `null` when `source` is `"company"`** on
 *    `XERO_TAX_TYPE_UNKNOWN` and `XERO_TAX_TYPE_NOT_REVENUE`, and that is
 *    fail-safe by design: a wrong company default fails every line, so naming
 *    one row would point at an arbitrary innocent line. The backend's own
 *    comment states the same rule — *"make the DANGEROUS direction the one you
 *    cannot reach by forgetting"* — at `xero-quote.payload.ts:1008`, where the
 *    one `origin` both refusals share is built as
 *    `lineId: source === 'line' ? line.lineId : null`.
 *
 *    ⚠️ **`XERO_NO_TAX_TYPE` does NOT obey this rule** — it sets `lineId` on
 *    every refusal, thrown before any `origin` exists, from
 *    `resolveLineTaxType` (`xero-quote.payload.ts:483`), with
 *    `lineId: line.lineId` written literally. That is safe only because rule 2
 *    already keeps that code's `source` off `"company"`.
 * 4. ⚠️ **`XERO_TAX_RATE_MISMATCH` is the exception, and it is the one that
 *    would bite.** There `lineId` is **always** set, *including* when the code
 *    was inherited from the company — that refusal is about the line's own
 *    `taxRate` disagreeing with the code's percentage, so the line is the
 *    subject whichever record supplied the code. `{source: 'company', lineId:
 *    9}` is a legitimate combination. **Never derive one field from the other
 *    in either direction**: not `lineId === null ⇒ company`, and not
 *    `source === 'company' ⇒ drop lineId`.
 *
 * `lineId` is the real `f_quote_products.id` — the id in a `quotes/9/products/9`
 * path — and **never an array index**. Confirmed in source: the line inputs are
 * built as `lineId: Number(line.id)` at `xero-quote.service.ts:279` and
 * `xero-invoice.service.ts:283`, straight off the row. The two readings are
 * indistinguishable on a one-line quote (row id and index are both small
 * integers) and diverge silently on every multi-line document — right by luck
 * on small data, wrong once it grows.
 */
export interface XeroTaxRefusalOrigin {
  /** `null` where the server did not say, or said something we do not know. */
  source: XeroTaxSource | null;
  /** The offending line's own id. `null` on the three type codes when `source` is `'company'`. */
  lineId: number | null;
  /** The product on that line, for copy that can name it. Never used as an id. */
  productName: string | null;
}

/** Narrow to something indexable without asserting anything about its keys. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Read the provenance off a refusal, or `null` when there is none to read.
 *
 * ## Why this goes through `detailPayload` and not through `details`
 *
 * `ApiError.details` is narrowed with `Array.isArray` and typed
 * `{path, message}[]` — the shape this API's own 422s use — while the raw body
 * is kept on `detailPayload: unknown` precisely so a differently-shaped
 * `details` survives. `source`, `lineId` and `productName` are **new fields on
 * that existing array shape**, not a new shape, and they are present on four
 * codes out of dozens. Widening `details` to declare them would tell every
 * reader in the app that any detail may carry a `lineId`, which is false and is
 * the kind of claim `tsc` then enforces in the wrong direction. So the type
 * stays honest and this one consumer pays a runtime guard, which is the trade
 * CLAUDE.md asks for: read `detailPayload` through a guard, never a cast.
 *
 * ## `null` is the behaviour we already had, and that is the whole design
 *
 * An older server, or a rollback, omits the three fields entirely, so every
 * caller must render exactly as it does today when this answers `null`.
 * Absent fields, a non-array `details`, no `details` at all, and an
 * unrecognised `source` value all land there, which means the fallback path is
 * the live path rather than a rare branch tested last. Same rule as
 * {@link quoteRefusalOf}, and for the same reason.
 *
 * **Not gated on the code**, deliberately. The fields are self-describing, the
 * gate would be a second list to keep in step with the backend's, and a fifth
 * code gaining provenance would otherwise degrade silently to the old copy with
 * nothing to show it had. The narrowing this feeds is applied per destination in
 * `XeroPanel`, so a `source` arriving on a code that is not in
 * {@link XERO_QUOTE_REFUSALS} changes nothing at all.
 *
 * The FIRST entry carrying any of the three wins. The refusals ship one detail;
 * scanning rather than reading `[0]` costs nothing and survives the day a
 * generic detail is prepended to them.
 */
export function refusalOriginOf(error: ApiError): XeroTaxRefusalOrigin | null {
  const payload = error.detailPayload;
  if (!Array.isArray(payload)) return null;

  for (const entry of payload) {
    if (!isRecord(entry)) continue;

    const source: XeroTaxSource | null =
      entry.source === 'line' || entry.source === 'company' ? entry.source : null;
    /*
     * `Number.isInteger` rather than `typeof === 'number'`: a row id is an
     * integer, and NaN, Infinity and 9.5 are all typeof number. A caller is
     * going to put this in a URL.
     */
    const lineId = typeof entry.lineId === 'number' && Number.isInteger(entry.lineId)
      ? entry.lineId
      : null;
    const productName =
      typeof entry.productName === 'string' && entry.productName.trim() !== ''
        ? entry.productName.trim()
        : null;

    if (source === null && lineId === null && productName === null) continue;
    return { source, lineId, productName };
  }

  return null;
}

/* -------------------------------------------------------------------------
 * Pushing a saved record onward, and reporting BOTH halves
 * ---------------------------------------------------------------------- */

/**
 * What happened to the Xero half of a save that has ALREADY SUCCEEDED here.
 *
 * ⚠️ **Every value in this union is reported alongside "the save succeeded",
 * never instead of it.** A CRM save and a push to an accounting system are two
 * requests with no transaction across them, so the only truthful report names
 * both — the same rule "One Save, reported honestly" states for a quote and its
 * lines, and for the same reason: **a rollback that was not performed must
 * never be claimed.** Nothing here has any power to undo the save, and nothing
 * here may imply it did.
 *
 * It is also why this is a union rather than an error-or-nothing. `formError ??
 * count` is catalogued in CLAUDE.md as a silent drop, and "saved" and "not sent
 * to Xero" are exactly two things that are true at once — coalescing them so
 * that one wins is the same bug in a new place.
 *
 * The members exist because they read differently to the person, not because
 * the server distinguishes them. ⚠️ **The count is deliberately not stated** —
 * this line said "the four outcomes" while listing five, and then a sixth was
 * added on 2026-08-30. A count in prose beside a list is the first thing to
 * rot.
 *
 * - `updated` — Xero's copy now matches. The only one that is purely good news.
 * - `settled` — Xero has moved the document past DRAFT, so it takes no more
 *   changes from here. **The ordinary outcome, not a fault** — see
 *   {@link isSettledInXeroCode} for why the tone matters more than the wording.
 * - `gone` — the Xero document was deleted there. The link has been stood down
 *   and needs detaching; the recovery lives in the Xero panel, and the copy
 *   here must point at it rather than restating it.
 * - `refused` — anything else the server said no to, routed by the existing
 *   {@link XERO_QUOTE_REFUSALS} machinery. `refusal: null` is a code newer than
 *   that map, and the caller MUST then fall back to `messages`.
 * - `unreachable` — the request never got an answer. Distinct from every
 *   refusal because nothing is known about Xero's copy either way, so no
 *   sentence may describe its state.
 */
export type XeroAutoPushOutcome =
  | { kind: 'updated' }
  | { kind: 'settled'; messages: string[] }
  | { kind: 'gone'; messages: string[] }
  | {
      kind: 'refused';
      code: string;
      /** `null` means "newer than the map" — fall back to `messages`, never guess. */
      refusal: XeroQuoteRefusal | null;
      /**
       * ⚠️ **`null` on everything the SERVER reports**, and that is a real
       * narrowing rather than an oversight. {@link refusalOriginOf} reads the
       * provenance off `ApiError.detailPayload`; the server's `xeroPush` report
       * carries a `code` and a `message` and nothing else, so a refusal that
       * arrived through a save has no `details` to read. The manual push still
       * throws an `ApiError` and still fills this in — see
       * {@link autoPushOutcomeOf}.
       */
      origin: XeroTaxRefusalOrigin | null;
      messages: string[];
    }
  | { kind: 'unreachable'; messages: string[] }
  /**
   * ⚠️ **THE PUSH DID NOT HAPPEN, AND NOTHING IS WRONG. Added 2026-08-30 with
   * the move of the push onto the server.**
   *
   * The server refuses to reach Xero at all for a document that has no lines
   * yet, or whose company nobody has linked, or that has never been sent to
   * Xero — and the last of those is the answer to **every save of every quote
   * and invoice that is not in Xero yet**, which is most of them. It is
   * checked before Xero is called, so it costs nothing against the tenant's
   * 5,000-a-day budget and it is not a refusal by Xero.
   *
   * ⚠️ **This has no counterpart in the four outcomes above, all of which
   * describe something Xero said.** Rendering it in their vocabulary — as a
   * refusal, or as an amber strip — would put a warning on every new document
   * in the system, which is the exact training-people-to-ignore-it failure
   * {@link SETTLED_IN_XERO} exists to prevent, at a hundred times the volume.
   *
   * `reason` is the server's own sentence and is stated as safe to show. It is
   * NEVER matched on: which skip this is cannot be read from its wording, and
   * a caller that needs to tell "not in Xero yet" from the rest asks the LINK,
   * which it already holds.
   */
  | { kind: 'skipped'; reason: string };

/**
 * What the SERVER did with the Xero half of a save, straight off the wire.
 *
 * ⚠️ **This is a sibling of `data`, not a field on it.** `POST`/`PATCH` on a
 * quote, an invoice and either one's line items answer
 * `{data, xeroPush}` — see `SaveEnvelope` in `@/lib/types`, which is the only
 * place that shape is declared.
 *
 * ⚠️ **`documentNumber` is Xero's number and it MOVES.** Xero re-numbers a
 * quote on any update that does not name its number, so this is what the
 * document is called *now*, not an identifier to match on later.
 */
export interface XeroPushPushed {
  status: 'pushed';
  /**
   * ⚠️ **CORRECTED 2026-08-31: A SAVE CREATES NOW.** *SUPERSEDED: "A save never
   * creates, by the owner's decision and enforced on the server, so this reads
   * `'updated'` on every push a save produces."* The owner reversed that in the
   * change that removed the "Send to Xero" button, so both members arrive — and
   * on the four CONVERSION endpoints `'created'` is the expected one, since the
   * document being pushed was made in the same request.
   *
   * The half that was right is why nothing had to move: it was declared honestly
   * rather than narrowed to one member, because the server's contract has both.
   * **A type that refuses a value the wire can carry is the claim this repo has
   * already been caught making** — and this is the entry where narrowing it would
   * have gone wrong within the fortnight.
   */
  kind: 'created' | 'updated';
  xeroId: string;
  /**
   * ⚠️ **THE ONLY NULLABLE MEMBER OF THIS VARIANT.** Xero may accept the document
   * and not have assigned it a number, so `null` is a real answer and not an
   * absence to paper over: rendered as `''` beside a label it reads as a number
   * Xero gave that we lost. Nothing renders it today — see
   * {@link conversionPushReport}.
   */
  documentNumber: string | null;
}

/** No push was attempted, and that is an ordinary state — see the union member. */
export interface XeroPushSkipped {
  status: 'skipped';
  /** The server's own sentence, stated as safe to show. Never matched on. */
  reason: string;
}

/**
 * Xero, or the server's own pre-flight, said no.
 *
 * ⚠️ **It carries a `code` and a `message` and nothing else** — no `details`,
 * so no {@link XeroTaxRefusalOrigin}. That is the one thing the save path knows
 * less than the manual push does.
 */
export interface XeroPushFailed {
  status: 'failed';
  code: string;
  message: string;
}

export type XeroPushReport = XeroPushPushed | XeroPushSkipped | XeroPushFailed;

/**
 * Classify a refusal from a push that nobody pressed a button for.
 *
 * The two special cases are read off the CODE before the refusal map is
 * consulted, and the order is deliberate: both are in
 * {@link XERO_QUOTE_REFUSALS} at `destination: 'nowhere'`, which is rendered as
 * a terminal failure — correct when a person pressed a button and got nothing,
 * and wrong here, where the push was not asked for and one of the two is the
 * expected answer. Same code, same map, different tone, decided in one place.
 *
 * ⚠️ **CORRECTED 2026-08-30. This took an `ApiError`**, because the push was a
 * second request this app made itself and a refusal arrived as a thrown error.
 * The push now happens inside the save on the server, so the refusal arrives as
 * `{code, message}` on the response body of a request that SUCCEEDED. The
 * narrowed parameter is what makes that legible: an `ApiError` reaching this
 * from a save would be a bug, and it can no longer be passed.
 *
 * The manual push in `XeroPanel` still throws, and a caller holding an
 * `ApiError` still reaches this by passing `{code: err.code, message:
 * err.message}` — one classifier, two entry points, so the two can never come
 * to mean different things by the same code.
 *
 * `messages` is passed in rather than derived, so this stays free of any
 * runtime dependency on `api.ts` — {@link xeroErrorLines} is what an
 * `ApiError` caller already uses, and it de-duplicates Xero's generic wrapper.
 * A caller reading a server report has one sentence and passes `[message]`.
 */
export function autoPushRefusalOutcome(
  failure: { code: string; message: string },
  messages: string[],
): XeroAutoPushOutcome {
  if (isSettledInXeroCode(failure.code)) return { kind: 'settled', messages };
  if (isGoneInXeroCode(failure.code)) return { kind: 'gone', messages };
  return {
    kind: 'refused',
    code: failure.code,
    refusal: XERO_QUOTE_REFUSALS.get(failure.code) ?? null,
    /*
     * ⚠️ Always `null` from here now. `refusalOriginOf` needs the 422's
     * `details` array, which a `xeroPush` report does not carry — see the
     * union member. The manual push keeps its provenance because `XeroPanel`
     * calls `refusalOriginOf` on the `ApiError` itself.
     */
    origin: null,
    messages,
  };
}

/**
 * Turn the server's own report on a save into the outcome the pages render.
 *
 * ⚠️ **`null` means "say nothing", and it is the ORDINARY answer**, not an
 * error path: a server that predates this contract omits `xeroPush` entirely,
 * and so does a rollback. Every caller must render exactly as it did before
 * `xeroPush` existed when this answers `null` — which is what both detail pages
 * already do for a push that was never appropriate.
 *
 * ⚠️ **`'pushed'` maps to `updated` whichever `kind` it carries, and the REASON
 * for that changed on 2026-08-31 while the mapping stayed right.** *SUPERSEDED:
 * "A save never creates, so `'created'` cannot arrive today; if it ever did,
 * 'Xero's copy now matches' is still a true sentence about it … If a save is ever
 * allowed to create, this is the line that has to be argued with."*
 *
 * **A save DOES create now** — owner's reversal, `xero-autopush.ts` in the
 * backend, taken in the change that removed the "Send to Xero" button — so
 * `'created'` is live, and on the four CONVERSION endpoints it is the expected
 * value, because a document that was made a moment ago has never been in Xero.
 *
 * The mapping is kept because the sentence it produces is true of both: Xero's
 * copy now matches, however it came to exist. ⚠️ **What it DISCARDS is
 * `documentNumber` and the create/update distinction**, and anything that wants
 * either must read {@link XeroPushReport} itself rather than widening this — the
 * `XeroAutoPushOutcome` union is what six render sites switch on, and giving
 * `updated` a payload would oblige every one of them to handle a number they have
 * no room for. Nothing renders the number today; see
 * {@link conversionPushReport} for where that was decided and why.
 */
export function autoPushOutcomeOf(
  report: XeroPushReport | null | undefined,
): XeroAutoPushOutcome | null {
  if (report == null) return null;
  if (report.status === 'pushed') return { kind: 'updated' };
  if (report.status === 'skipped') return { kind: 'skipped', reason: report.reason };
  if (report.status === 'failed') return autoPushRefusalOutcome(report, [report.message]);
  /*
   * A status this build has never heard of. Saying nothing is the only honest
   * rendering — the statuses named above are the whole vocabulary this build
   * knows, and guessing which one a newer status resembles is how a refusal
   * comes to be reported as a success.
   */
  return null;
}

/* -------------------------------------------------------------------------
 * The push control on a LIST ROW
 *
 * `/quotes` and `/invoices` both offer one control per row: create the Xero
 * counterpart, or update it. All of
 * it — the label, the enable/disable decision, the disabled sentence, the
 * confirmation copy and the outcome copy — is decided HERE and rendered by one
 * component, so the two pages cannot word or gate it differently. That is not
 * tidiness: this repo has already shipped one refusal described in two tones
 * across the quote and the invoice page, and a hint fixed on one form that
 * survived on the other because a grep for the fixed wording did not find it.
 * ---------------------------------------------------------------------- */

/**
 * ⚠️ **THE DISABLED STATE IS A CLAIM, AND THE ASYMMETRY IS NOT CLOSE.**
 *
 * "Nothing new to send" is a sentence this app would be asserting about SEED's
 * accounting system. If it is wrong, somebody edits a record, is told there is
 * nothing to send, and a stale document sits in the accounts with nobody looking
 * for it — with the UI having actively told them not to look.
 *
 * - A button wrongly ENABLED costs one redundant push of an identical payload.
 * - A button wrongly DISABLED is a false statement that leaves a stale document
 *   in the accounts.
 *
 * **So: when in doubt, ENABLE.** `disabled` is reserved for the cases below,
 * each of which is provable from data in hand and each of which carries its own
 * sentence — an unnameable disabled state is a bug, not a default.
 *
 * ## ⚠️ THERE IS NO CLIENT-DERIVABLE "NOTHING TO SEND" TEST. DO NOT REBUILD ONE
 *
 * The obvious derivation — a linked row is unchanged when
 * `updatedAt <= link.lastSyncedAt`, reading *"No changes recorded since the last
 * send."* — is cheap and wrong, and the next person will reach for it exactly as
 * we did. Here is the measurement that kills it:
 *
 * - `recalculateTotals` runs on all six line-write paths but writes **only the
 *   four total columns, never `updated_at`**. `updated_at` is `ON UPDATE
 *   current_timestamp()`, which fires only when a value actually **changes**.
 * - `line_subtotal` / `line_tax` / `line_total` are GENERATED from quantity, unit
 *   price, discount and tax rate **only**. So a **`description`, `cityIds` or
 *   `inclusions`** edit cannot move a total, the UPDATE writes the same four
 *   values back, the parent row is not changed, and **`updated_at` does not
 *   move**. All three of those reach the customer's Xero document: `description`
 *   is pushed as the line's own text, and cities and inclusions feed the
 *   composed line a customer reads.
 * - Worse: **a cities-only or inclusions-only PATCH issues no UPDATE at all.**
 *   Neither key is in `WRITABLE_COLUMNS`, so the repository hits
 *   `if (assignments.length === 0) return false`. **Not even the LINE's own
 *   `updatedAt` moves**, so there is no fallback timestamp to fall back to.
 * - Even the money case is not reliably a yes. A price edit on a line whose unit
 *   price is NULL or `0.00` leaves every total at `0.00`, so nothing changes.
 *   Production holds 16 products with no price and 12 at `0.00` from the Xero
 *   import — this is reachable in real data, not a corner.
 * - And there is a **one-shot opposite case**: `discount_total` and `tax_total`
 *   are nullable, so on a record holding NULL the *first* line mutation of any
 *   kind writes `0.00`, which IS a change. `updated_at` moves once and then never
 *   again for that class. **A rule that appears to work on the first edit and
 *   silently stops is the worst possible way to learn this** — it buys the
 *   derivation a demonstration before it starts lying.
 *
 * So the feared scenario is exact, not theoretical: someone edits a line's
 * cities, the button says there is nothing to send, and the customer receives a
 * different sentence from the one on screen.
 *
 * **There is no client-derivable "nothing to send" test today.** Not from
 * `updatedAt`, not from the line's `updatedAt`, not from the totals. A linked row
 * {@link pushIntentFor} says can be updated is **always enabled**.
 *
 * ## The honest future is a server-supplied fact
 *
 * The backend is building an explicit **`stale` flag on the link row**, approved
 * by the owner — the server knows what it wrote and when, which is precisely the
 * knowledge no client-side inference has. When it lands it is a fact we render,
 * not an inference we make. {@link hasUnsentChanges} is the single place that
 * answers "is there anything to send"; it answers `true` unconditionally today,
 * and adopting the flag is that function's body plus a `link` argument.
 */
export type XeroRowPushDecision =
  | {
      enabled: true;
      /** For copy and reporting. Never for deciding — see {@link pushIntentFor}. */
      action: 'create' | 'update';
      label: string;
      /** Minted by {@link pushIntentFor}; `api.pushToXero` will not move without it. */
      intent: XeroPushIntent;
    }
  | {
      enabled: false;
      /** Still says what the action WOULD be, so a disabled row still reads. */
      label: string;
      /** Named, always. A disabled control with no stated reason is the bug. */
      reason: string;
    };

/** `Push to Xero` creates; `Sync to Xero` updates. Two verbs, one per branch. */
const CREATE_LABEL = 'Push to Xero';
const UPDATE_LABEL = 'Sync to Xero';

/**
 * Is there anything to send to Xero for this linked record?
 *
 * ⚠️ **Today the only honest answer is `true`, always** — and the reason is
 * measured, not cautious. See the "THERE IS NO CLIENT-DERIVABLE 'NOTHING TO
 * SEND' TEST" block on {@link XeroRowPushDecision}: a `description`, `cityIds`
 * or `inclusions` edit reaches the customer's Xero document and moves **no**
 * timestamp this app can read, so every comparison available here answers "no
 * changes" about a record that has really changed.
 *
 * This function exists so that stays **one** decision in **one** place. It takes
 * no arguments because nothing it could be given is evidence.
 *
 * **Adopting the backend's `stale` flag is this body and this signature.** When
 * the link row carries it, this becomes `hasUnsentChanges(link)` returning
 * `link.stale`, and `rowPushDecision` gains one argument at one call site.
 * Nothing else in the app has to learn about it.
 *
 * ⚠️ **Do not reinstate a timestamp comparison here.** A server-supplied `stale`
 * is a fact the writer recorded; `updatedAt <= lastSyncedAt` is a guess about
 * what the writer did, and it is a wrong one.
 */
export function hasUnsentChanges(): boolean {
  return true;
}

/**
 * What the push control on one list row should be.
 *
 * **Layered on {@link pushIntentFor}, never around it.** That function is the
 * single decision point for create-vs-update in this app, and it is the only
 * producer of the branded intent `api.pushToXero` demands — so a call site
 * cannot express "create" and silently get "update". This adds two things it
 * does not answer: whether the user may press the button at all, and whether
 * there is anything to send.
 *
 * `denied` is the permission sentence from `denialReason(ability, 'update',
 * entity)`, or `null` when the user may write. It is passed in rather than
 * derived so this file stays free of the ability model; the one caller is the
 * shared control, which derives it once.
 *
 * The precedence is deliberate: permission first (there is no point naming a
 * data reason to somebody who could not act on it either way), then the link's
 * own state, then whether there is anything to send.
 *
 * ⚠️ There is deliberately no `updatedAt` parameter and no freshness branch —
 * see {@link XeroRowPushDecision} for the measurement showing no timestamp this
 * app can read proves a linked row is unchanged.
 */
export function rowPushDecision(
  entity: XeroEntity,
  link: XeroLink,
  denied: string | null,
): XeroRowPushDecision {
  const intent = pushIntentFor(entity, link);
  /*
   * The label describes what the action WOULD be even where it is refused, so a
   * disabled row still tells the reader which side of the create/update line the
   * record sits on. Derived from the id for the same reason `canCreateInXero`
   * is: a `failed` link sits on either side, so status cannot answer it.
   */
  const label = link.id === null ? CREATE_LABEL : UPDATE_LABEL;

  if (denied !== null) return { enabled: false, label, reason: denied };

  if (!XERO_ENTITIES[entity].capabilities.create) {
    return {
      enabled: false,
      label,
      reason: `${XERO_ENTITIES[entity].plural} are not wired to Xero in this app.`,
    };
  }

  if (intent === null) {
    /*
     * `pushIntentFor` refused. The two states worth their own sentence are the
     * ones where the existing one would be actively wrong: a link stood down
     * because the Xero document was DELETED still reads `archived`, and telling
     * somebody the record "is archived in Xero" sends them there to look for a
     * document that is not there. Everything else takes `statusDetail`, which is
     * already the one-sentence-you-can-act-on for each status and is the copy the
     * badge beside this control shows — one model, not a second set of words.
     */
    if (isStoodDownAsGone(link)) {
      return {
        enabled: false,
        label,
        reason: `The Xero ${XERO_ENTITIES[entity].xeroName.toLowerCase()} was deleted in Xero. Unlink it on the ${XERO_ENTITIES[entity].label} first.`,
      };
    }
    return { enabled: false, label, reason: statusDetail(link.status) };
  }

  /*
   * A CREATE could never be disabled for having nothing to send in any case:
   * there is nothing in Xero, so the whole document is new.
   */
  if (intent.action === 'create') {
    return { enabled: true, action: 'create', label: CREATE_LABEL, intent };
  }

  /*
   * ⚠️ **This is the only gate left on an UPDATE, and it currently always
   * opens.** {@link hasUnsentChanges} answers `true` unconditionally, so every
   * linked row {@link pushIntentFor} accepts is ENABLED — deliberately, because
   * no timestamp this app can read is moved by a `description`, `cityIds` or
   * `inclusions` edit, and all three change what the customer receives. The
   * measurement is on {@link XeroRowPushDecision}; read it before adding any
   * condition here.
   *
   * The branch is kept rather than deleted because it is the seat the backend's
   * `stale` flag drops into, and the sentence below is only sayable BY that
   * flag: the server knows what it wrote. Do not make it reachable from anything
   * derived on this side.
   */
  if (!hasUnsentChanges()) {
    return {
      enabled: false,
      label: UPDATE_LABEL,
      reason: 'Xero already has this version.',
    };
  }

  return { enabled: true, action: 'update', label: UPDATE_LABEL, intent };
}

/** The three strings a confirmation needs, so neither page writes its own. */
export interface XeroPushConfirmCopy {
  title: string;
  body: string;
  confirmLabel: string;
}

/**
 * What pressing the button will actually do, named record and real consequence.
 *
 * ⚠️ **Do NOT warn about renumbering.** Xero does not renumber on update: the
 * backend pins `QuoteNumber` from the live Xero document on every update path,
 * because Xero is the authority on its own numbering.
 *
 * The update sentence names the safe case and the unsafe one in that order,
 * because the safe one is the COMMON one: once a document has been sent from
 * Xero the push is refused (`XERO_QUOTE_NOT_DRAFT` / `XERO_INVOICE_NOT_DRAFT`,
 * both in {@link SETTLED_IN_XERO}) and nothing there is touched. A DRAFT is the
 * case with no second guard, so it is the one the sentence ends on.
 */
export function pushConfirmCopy(
  entity: XeroEntity,
  action: 'create' | 'update',
  recordName: string,
): XeroPushConfirmCopy {
  const { label, xeroName } = XERO_ENTITIES[entity];
  const noun = xeroName.toLowerCase();

  if (action === 'create') {
    return {
      title: `Create this ${label} in Xero?`,
      body:
        `“${recordName}” has no counterpart in Xero. This creates a new draft ${noun} ` +
        `there, inside SEED's accounting system. Nothing in EMERGE can withdraw it — ` +
        `only somebody with Xero access can delete it, in Xero.`,
      confirmLabel: CREATE_LABEL,
    };
  }

  return {
    title: `Overwrite this ${label} in Xero?`,
    body:
      `This replaces the Xero ${noun} for “${recordName}” with what EMERGE holds now. ` +
      `Once Xero's copy has been sent, Xero refuses the change and nothing there is ` +
      `altered — but a draft is replaced silently, with no further warning.`,
    confirmLabel: UPDATE_LABEL,
  };
}

/**
 * The warning shown beside an UPDATE control on a list row, which fires on
 * click with no confirmation dialog.
 *
 * ⚠️ **A CREATE still confirms** — it puts a new document into the accountant's
 * Xero that only a person can delete there — while an UPDATE does not, because
 * overwriting a draft already pushed is low-stakes and confirming it every time
 * is wearing. {@link pushConfirmCopy}'s `update` branch is not reached from the
 * list for that reason; it still serves the detail-page flow, which is a
 * separate control.
 *
 * This carries the two facts a reader needs and nothing else: the click IS the
 * commit, and the thing it replaces is real and in Xero. It deliberately does
 * NOT repeat that Xero refuses the change once its copy has been sent — that
 * case is the ordinary answer, not a warning, and {@link rowPushReport} already
 * reports it as `settled` in a neutral tone after the fact.
 */
export function updatePushCaution(entity: XeroEntity): string {
  const noun = XERO_ENTITIES[entity].xeroName.toLowerCase();
  return `Sends immediately, with no confirmation, and replaces the draft ${noun} in Xero.`;
}

/**
 * How a row push ended. `pushed` is the only good outcome; the rest are all
 * reported, none of them are retried, and `settled` is not a fault.
 */
export type XeroRowPushResult =
  | { kind: 'pushed'; action: 'create' | 'update' }
  | { kind: 'settled'; messages: string[] }
  | { kind: 'gone'; messages: string[] }
  | {
      kind: 'refused';
      code: string;
      /** `null` means "newer than the map" — fall back to `messages`, never guess. */
      refusal: XeroQuoteRefusal | null;
      origin: XeroTaxRefusalOrigin | null;
      messages: string[];
    }
  | { kind: 'denied'; messages: string[] }
  | { kind: 'unreachable'; messages: string[] };

/**
 * Classify a failed row push.
 *
 * **403 is separated first and never reaches the refusal machinery.** A role can
 * change under a live access token, which lasts over five days, so a gated
 * control is an optimisation and this branch is the guarantee. A 403 body carries
 * **no `details`** (unlike a 422), so it is reported as a banner and never as a
 * per-field error.
 *
 * Everything else goes through {@link autoPushRefusalOutcome}, which is where the
 * settled-before-gone-before-refused ordering already lives — so a deliberate
 * push and an automatic one cannot disagree about what a code means. Only the
 * TONE differs between them, and that is decided in {@link rowPushReport}.
 */
export function rowPushFailure(error: ApiError, messages: string[]): XeroRowPushResult {
  if (error.status === 403) return { kind: 'denied', messages };

  const outcome = autoPushRefusalOutcome(error, messages);
  switch (outcome.kind) {
    case 'settled':
      return { kind: 'settled', messages: outcome.messages };
    case 'gone':
      return { kind: 'gone', messages: outcome.messages };
    case 'refused':
      return {
        kind: 'refused',
        code: outcome.code,
        refusal: outcome.refusal,
        origin: outcome.origin,
        messages: outcome.messages,
      };
    default:
      /*
       * `autoPushRefusalOutcome` classifies errors only, so `updated` and
       * `unreachable` are unreachable here. Kept rather than asserted away: a
       * sixth member added to that union should land somewhere honest rather
       * than throwing inside an error handler.
       */
      return { kind: 'unreachable', messages };
  }
}

/** One report, rendered identically wherever a row push ends. */
export interface XeroRowPushReport {
  tone: XeroTone;
  heading: string;
  /** The SERVER's own sentences. Always rendered; never replaced by ours. */
  lines: string[];
  /** Where to go next, or `null` where there is nowhere to send anyone. */
  pointer: string | null;
}

/**
 * WHERE a refusal sends the reader, at list scale.
 *
 * ⚠️ **Deliberately NOT a second copy of `XeroPanel`'s destination prose.** That
 * component says, in full, what to change and on which record; repeating any of
 * it here would be two sets of words for one refusal, which is exactly the
 * divergence this whole file argues against. A list row is not the place to
 * explain a Xero tax code — it is the place to say which record to open. So every
 * in-app destination collapses to one pointer at the detail page, where the panel
 * already does the explaining.
 *
 * The two that do NOT collapse are the two where opening the record would waste
 * the reader's time: `xero-settings` has no EMERGE field to correct at all, and
 * `nowhere` is terminal.
 *
 * ⚠️ **`null` for an unrecognised refusal, and that is required rather than
 * lazy.** {@link quoteRefusalOf} answers `null` for a code newer than its map,
 * and inventing a destination for one would send somebody to edit a document over
 * a problem in Xero's settings with no way to tell the guidance was guessed. The
 * server's own message is always rendered above this, so `null` costs nothing.
 */
function refusalPointer(entity: XeroEntity, refusal: XeroQuoteRefusal | null): string | null {
  if (refusal === null) return null;
  switch (refusal.destination) {
    case 'nowhere':
      return null;
    case 'xero-settings':
      return 'Nothing in EMERGE can fix this — the change is in Xero’s own settings.';
    default:
      return `Open the ${XERO_ENTITIES[entity].label} — its Xero panel says what to change, and where.`;
  }
}

/**
 * The report for one outcome: tone, heading, the server's lines, and a pointer.
 *
 * ⚠️ **`settled` is toned `info`, not `danger`, and that is the whole point of
 * {@link SETTLED_IN_XERO}.** Once a document has been sent from inside Xero every
 * later push is refused, so on a list this will be the COMMON answer — and
 * rendering the ordinary working of the system as a failure teaches people that
 * the Xero notice means nothing, after which the refusals that do need acting on
 * are ignored too.
 *
 * ⚠️ **`unreachable` must never claim Xero was not written to.** Nothing is known
 * about Xero's copy either way, so the copy says exactly that and sends the reader
 * to look rather than inviting a retry that could produce a second document.
 */
export function rowPushReport(
  entity: XeroEntity,
  recordName: string,
  result: XeroRowPushResult,
): XeroRowPushReport {
  const { label, xeroName } = XERO_ENTITIES[entity];
  const noun = xeroName.toLowerCase();

  switch (result.kind) {
    case 'pushed':
      return {
        tone: 'success',
        heading:
          result.action === 'create'
            ? `Created “${recordName}” in Xero as a draft ${noun}.`
            : `Sent “${recordName}” to Xero.`,
        /*
         * ⚠️ The update line is the SECOND home of the warning the removed
         * confirmation used to carry — see {@link updatePushCaution}, which is the
         * first and is on screen BEFORE the click. This one is after the fact, and
         * it is what a person who clicked without reading actually sees.
         *
         * It claims only what a 2xx proves. Xero accepted the write, so its copy
         * now holds what EMERGE sent and no longer holds what it held before; had
         * the document been sent from inside Xero this would have been refused and
         * classified `settled` instead. It deliberately does not say "draft",
         * which is an inference about Xero's state rather than something the
         * response carries.
         */
        lines:
          result.action === 'update'
            ? [`Xero accepted it, so the ${noun} there was replaced with what EMERGE holds now.`]
            : [],
        pointer: null,
      };

    case 'settled':
      return {
        tone: 'info',
        heading: `Xero has already sent the ${noun} for “${recordName}”.`,
        lines: [
          ...result.messages,
          'Xero takes no more changes to it, so nothing there was altered.',
        ],
        pointer: null,
      };

    case 'gone':
      return {
        tone: 'warning',
        heading: `The Xero ${noun} for “${recordName}” was deleted in Xero.`,
        lines: [...result.messages],
        pointer: `The link here still holds its id. Open the ${label} and unlink it before creating a fresh one.`,
      };

    case 'refused': {
      /*
       * `origin.productName` is the product on the offending LINE, shipped by
       * the backend on the four tax refusals. It is added rather than
       * substituted: the server's own sentences carry the percentages and the
       * code names, and none of that may be dropped in favour of a product name.
       */
      const onLine =
        result.origin?.productName !== null && result.origin?.productName !== undefined
          ? [`On the line for “${result.origin.productName}”.`]
          : [];
      return {
        tone: 'danger',
        heading: `Xero refused the ${label} “${recordName}”.`,
        lines: [...result.messages, ...onLine],
        pointer: refusalPointer(entity, result.refusal),
      };
    }

    case 'denied':
      return {
        tone: 'danger',
        heading: `You are not allowed to send this ${label} to Xero.`,
        lines: [...result.messages],
        pointer: null,
      };

    case 'unreachable':
      return {
        tone: 'danger',
        heading: `We could not tell whether Xero received “${recordName}”.`,
        lines: [
          ...result.messages,
          `The request did not come back, so nothing is known about Xero’s copy either way. Open the ${label} and check before sending again.`,
        ],
        pointer: null,
      };
  }
}

/**
 * ── WHAT A CONVERSION'S OWN PUSH HAS TO SAY BEFORE THE PERSON IS TAKEN AWAY ──
 *
 * The four creation endpoints — `POST /quotes/{id}/convert`,
 * `POST /quotes/{id}/invoices`, `POST /deals/{id}/convert` and
 * `POST /deals/{id}/invoices` — each push the document they CREATED and report it
 * in `xeroPush` beside `data`. This turns that report into the band a conversion
 * screen shows, or `null` for "say nothing and carry on".
 *
 * ⚠️ **`xeroPush` DESCRIBES `data`, NEVER THE RECORD IN THE URL.** Every one of
 * the four calls `autoPushAndRefresh` on the document it just made, so a quote
 * conversion's report is about the INVOICE and a deal conversion's is about the
 * QUOTE or the invoice it raised. `entity` is therefore the entity of `data` —
 * a caller passing the URL's entity would label a refusal about an invoice as one
 * about the quote it came from, and send the reader to the wrong record.
 *
 * ⚠️ **`null` FOR `pushed` AND `skipped`, AND THAT IS THE DECISION HERE.** A
 * conversion navigates to the document it made, and that document's own Xero
 * panel is the record of what is true now:
 *
 * - `pushed` is good news and nothing is owed. Holding somebody on a modal to
 *   tell them a thing worked, when the page they are about to land on says it,
 *   buys nothing and trains them to click past the band that matters.
 * - `skipped` is the ORDINARY answer and by some margin the commonest: the server
 *   returns it, before calling Xero at all, for a document with no line items and
 *   for a company nobody has linked to a Xero contact — and 263 of 413 companies
 *   were unlinked when this was written. The destination's Xero panel already
 *   reads "not linked". Rendering it here would put a warning band on the
 *   majority of conversions in the system, which is the exact
 *   teach-people-to-ignore-it failure {@link SETTLED_IN_XERO} exists to prevent.
 * - `settled` is reached only through a `failed` whose code
 *   {@link isSettledInXeroCode} downgrades, and it means Xero has already acted
 *   on the document. Ordinary, `info`, and about a state a brand-new document
 *   cannot be in — so it is suppressed with the other two rather than given copy
 *   nobody will ever read.
 *
 * ⚠️ **WHICH LEAVES `failed`, AND IT IS INVISIBLE EVERYWHERE ELSE.** The server
 * swallows the failure so the conversion still answers `201`, and it does NOT
 * re-read the record on that branch **because nothing was written** — so the
 * document arrives with no Xero link, indistinguishable on the destination page
 * from one nobody has pushed yet. This report is the only artefact anywhere that
 * carries it. A caller that drops it has created a real document in the
 * accountant's system, told the person it worked, and left no trace of the fact
 * that Xero has not got it.
 *
 * ⚠️ **THE HEADING LEADS WITH THE DOCUMENT HAVING BEEN CREATED, and that is
 * load-bearing rather than courteous.** `POST /deals/{id}/invoices` answers **no
 * 409**, so a person who reads a refused push as "the conversion failed" and
 * presses it again gets a second invoice, and a second demand for money against
 * one customer, with nothing anywhere refusing it. {@link rowPushReport}'s own
 * headings are written for a list row where nothing was just created, so the
 * heading is replaced and only its LINES — the server's own sentences — and its
 * pointer are kept. Nothing here invents a sentence about the cause.
 */
export function conversionPushReport(
  entity: XeroEntity,
  recordName: string,
  report: XeroPushReport | null | undefined,
): XeroRowPushReport | null {
  const outcome = autoPushOutcomeOf(report);
  /*
   * `null` covers an absent report — a server predating the contract, or a
   * rollback — and a status this build has never heard of. Both are decided in
   * `autoPushOutcomeOf` and neither is something to guess a tone for.
   */
  if (outcome === null) return null;
  if (outcome.kind === 'updated' || outcome.kind === 'skipped' || outcome.kind === 'settled') {
    return null;
  }

  /*
   * The CLASSIFICATION and every sentence come from `rowPushReport` — the same
   * pair the list rows, both detail pages and the create path in
   * `InvoiceFormModal` use. A third wording of one server code is precisely the
   * drift this module was consolidated to prevent.
   */
  const base = rowPushReport(entity, recordName, outcome);
  return {
    ...base,
    heading: `“${recordName}” was created, and Xero would not take it.`,
  };
}

/**
 * A deep link into Xero's own web app.
 *
 * `shortCode` comes from the connection and is the tenant's own path segment —
 * the same organisation has a different one for a different Xero login, which
 * is why it is read from the API rather than built into a constant. Without it
 * there is no correct URL, so this returns null rather than guessing one that
 * lands on the wrong organisation.
 */
export function xeroDeepLink(
  entity: XeroEntity,
  xeroId: string | null,
  shortCode: string | null,
): string | null {
  const path = XERO_ENTITIES[entity].xeroAppPath;
  if (!path || !xeroId || !shortCode) return null;
  return `https://go.xero.com/organisationlogin/default.aspx?shortcode=${encodeURIComponent(
    shortCode,
  )}&redirecturl=/${path}/${encodeURIComponent(xeroId)}`;
}

/* -------------------------------------------------------------------------
 * Match confidence, for the link picker
 * ---------------------------------------------------------------------- */

/**
 * A candidate Xero contact offered when linking a company by hand.
 *
 * Scored by the backend, not here — matching has to run against all 938
 * contacts, and shipping that list to the browser to score it would be both
 * slow and a needless disclosure of the whole vendor ledger.
 */
export interface XeroContactMatch {
  xeroId: string;
  name: string;
  email: string | null;
  /** 0–1. Shown as a band, never as a number — see {@link confidenceBand}. */
  score: number;
  /** Why it matched, in words: "same name", "same email domain". */
  reason: string;
  /** Already attached to a different EMERGE record — linking would fork it. */
  takenBy: { entity: XeroEntity; id: number; name: string } | null;
  /**
   * Whether this contact has ever been invoiced. The tenant is ~92% expense
   * vendors, so this is the signal that separates "University of Warwick" from
   * "Metro taxi" in a picker that would otherwise sort them together.
   */
  isLikelyCustomer: boolean;
  /** Archived contacts are shown but never selectable. */
  isArchived: boolean;
}

export type ConfidenceBand = 'exact' | 'strong' | 'possible' | 'weak';

/**
 * Scores are rendered as one of four words, never as "87%".
 *
 * A percentage invites the reader to treat a fuzzy string comparison as a
 * measurement and to accept 87% without looking. The bands are chosen so that
 * anything below `exact` visibly requires a decision.
 */
export function confidenceBand(score: number): ConfidenceBand {
  if (score >= 0.99) return 'exact';
  if (score >= 0.85) return 'strong';
  if (score >= 0.6) return 'possible';
  return 'weak';
}

const BAND_LABELS: Readonly<Record<ConfidenceBand, string>> = {
  exact: 'Exact name match',
  strong: 'Very likely',
  possible: 'Possible',
  weak: 'Unlikely',
};

const BAND_TONES: Readonly<Record<ConfidenceBand, XeroTone>> = {
  exact: 'success',
  strong: 'info',
  possible: 'warning',
  weak: 'neutral',
};

export function bandLabel(band: ConfidenceBand): string {
  return BAND_LABELS[band];
}

export function bandTone(band: ConfidenceBand): XeroTone {
  return BAND_TONES[band];
}

/**
 * Order for the link picker: customers first, then confidence, then name.
 *
 * Customer-before-confidence is the load-bearing part. A vendor called "Ocean
 * Pearl" can score an exact match on a company someone typed as "Ocean Pearl",
 * and putting that above an invoiced university is how the wrong link gets
 * made. Archived candidates sink to the bottom regardless.
 */
export function compareMatches(a: XeroContactMatch, b: XeroContactMatch): number {
  if (a.isArchived !== b.isArchived) return a.isArchived ? 1 : -1;
  if (a.isLikelyCustomer !== b.isLikelyCustomer) return a.isLikelyCustomer ? -1 : 1;
  if (a.score !== b.score) return b.score - a.score;
  return a.name.localeCompare(b.name);
}

/**
 * A candidate that is already spoken for can still be linked, but doing so
 * points two EMERGE records at one Xero contact — after which a push from
 * either overwrites the other. Always a confirmation, never a silent allow.
 */
export function isForkingLink(match: XeroContactMatch): boolean {
  return match.takenBy !== null;
}

/* -------------------------------------------------------------------------
 * The connection
 * ---------------------------------------------------------------------- */

/**
 * The tenant this app is wired to, as our API reports it.
 *
 * A Xero login can grant several organisations, so `tenantName` is displayed
 * everywhere a sync decision is made. Pushing SEED's records into the wrong
 * organisation is silent, and undoing it means deleting records inside an
 * accounting system — so the organisation name is never left implicit.
 */
export interface XeroConnection {
  /**
   * Whether the SERVER holds Xero credentials at all.
   *
   * Distinct from `connected`, and the distinction is the whole reason the
   * key exists: `configured: false` means nobody in this app can do
   * anything about it — there is no client id on the server — so offering a
   * "Connect Xero" button would be offering one that fails. `configured:
   * true, connected: false` is the actionable state, and the only one where
   * that button belongs.
   */
  configured: boolean;
  connected: boolean;
  /**
   * Machine-readable reason for a falsy `connected` — `not_configured`,
   * `not_connected`, and whatever the server adds later. Switch on this,
   * never on prose. Typed loosely on purpose so an unrecognised value is a
   * value to fall back on rather than a compile error on deploy day.
   */
  reason?: string | null;
  tenantId: string | null;
  tenantName: string | null;
  /** Xero's own tenant path segment, needed by {@link xeroDeepLink}. */
  shortCode: string | null;
  /** The tenant's base currency. Ours are per-record and never converted. */
  baseCurrency: string | null;
  connectedAt: string | null;
  /** Who authorised it, so an expiry has someone to chase. */
  connectedBy: { id: number; name: string } | null;
  /**
   * When the REFRESH token dies. Xero's refresh token lasts 60 days and is
   * renewed on every use, so this only matters when nothing has synced for
   * two months — at which point the connection is gone and must be
   * re-authorised by hand. Surfaced early rather than discovered on the day.
   */
  expiresAt: string | null;
}

export const NO_CONNECTION: XeroConnection = {
  configured: false,
  connected: false,
  reason: null,
  tenantId: null,
  tenantName: null,
  shortCode: null,
  baseCurrency: null,
  connectedAt: null,
  connectedBy: null,
  expiresAt: null,
};

/** Xero's refresh token dies after 60 days idle; warn while it is still fixable. */
export function connectionExpiringSoon(connection: XeroConnection, days = 7): boolean {
  if (!connection.connected || !connection.expiresAt) return false;
  const remaining = new Date(connection.expiresAt).getTime() - Date.now();
  return remaining > 0 && remaining < days * 86_400_000;
}

/** Per-entity counts for the Integrations page. */
export interface XeroSyncSummary {
  entities: readonly XeroEntitySummary[];
  /**
   * Oldest unfinished job, and the only field that separates "the queue is
   * empty" from "the worker is dead" — no per-entity count can tell them apart.
   *
   * **Three states, and collapsing any two of them is how a page lies.**
   * A timestamp means something is waiting, and a value that stops moving while
   * the counts stand still means the worker has stopped draining. `null` is the
   * genuine all-clear. `undefined` means the server did not report it at all,
   * which must never render as an all-clear — that is a claim built on missing
   * data, and it would read as reassurance at exactly the moment it mattered.
   *
   * The server counts queued AND running jobs (a job wedged in `running` by a
   * worker that died mid-push is precisely what this exists to reveal), excludes
   * `failed` (retries exhausted, waiting on a human — counting it would report a
   * dead queue forever), and reads `created_at` rather than the backoff's next
   * attempt time, so a struggling queue does not look younger the more it has
   * struggled.
   */
  oldestPendingAt?: string | null;
}

export interface XeroEntitySummary {
  entity: XeroEntity;
  total: number;
  synced: number;
  pending: number;
  failed: number;
  conflict: number;
  archived: number;
  unlinked: number;
}

/**
 * The summary EXACTLY as `/xero/summary` sends it, which is not the shape
 * the UI wants.
 *
 * Kept as a separate type with an adapter rather than reshaping the UI
 * around it: the wire form nests the counts under `byStatus`, names the key
 * `entityType`, and carries a server-rendered `label` we already have in the
 * registry. Absorbing all of that into the components would spread one
 * server-side naming choice across every card. The adapter is the one place it
 * lands, so if the API is aligned later this file is the only edit.
 */
export interface XeroEntitySummaryWire {
  entityType: string;
  label: string;
  total: number;
  byStatus: Partial<Record<XeroSyncStatus, number>>;
}

/**
 * Wire rows to the flat shape the cards render.
 *
 * Unknown `entityType` values are DROPPED rather than rendered: the registry
 * is what supplies the label and the Xero-side name, and a row it does not
 * know would render as `undefined`. A fifth entity appearing server-side is
 * a prompt to add a registry entry, not something to half-display.
 *
 * Missing counts read as 0. The server omits a status it has none of, and an
 * absent count means none — not unknown.
 */
export function normaliseSummary(
  rows: readonly XeroEntitySummaryWire[],
  meta?: { oldestPendingAt?: string | null },
): XeroSyncSummary {
  const entities: XeroEntitySummary[] = [];
  for (const row of rows) {
    if (!isXeroEntity(row.entityType)) continue;
    const counts = row.byStatus ?? {};
    entities.push({
      entity: row.entityType,
      total: row.total,
      synced: counts.synced ?? 0,
      pending: counts.pending ?? 0,
      failed: counts.failed ?? 0,
      conflict: counts.conflict ?? 0,
      archived: counts.archived ?? 0,
      unlinked: counts.unlinked ?? 0,
    });
  }
  /*
   * Carried through from `meta`, where it belongs: it describes the queue as a
   * whole rather than any one entity. Left `undefined` when the key is absent
   * rather than defaulted to null — see the field comment for why those two
   * must stay distinguishable.
   */
  return meta && 'oldestPendingAt' in meta
    ? { entities, oldestPendingAt: meta.oldestPendingAt ?? null }
    : { entities };
}

/** Narrowing guard, so an unknown slug never indexes the registry. */
export function isXeroEntity(value: string): value is XeroEntity {
  return Object.prototype.hasOwnProperty.call(XERO_ENTITIES, value);
}

/** Anything an operator must act on. Drives the "needs attention" count. */
export function needsAttention(summary: XeroEntitySummary): number {
  return summary.failed + summary.conflict;
}

/*
 * Xero's own top-level wrapper message. It appears on every validation
 * failure, says nothing, and is the FIRST entry the API's extractor collects —
 * so `messages[0]`, which is what the server puts on `error.message`, is
 * reliably the useless one whenever a useful one exists.
 *
 * Matched by exact text rather than by position: depending on the extractor's
 * ordering would break silently the day it changes, and a wording change here
 * only costs us one extra line on screen.
 */
const XERO_GENERIC_MESSAGES: ReadonlySet<string> = new Set(['a validation exception occurred']);

/**
 * The lines to actually show a person when Xero refuses a write.
 *
 * Xero nests the real reason — "Additional people cannot be added when the
 * primary person has no email address set" — inside `Elements[].ValidationErrors`
 * and puts a generic sentence at the top level. Showing only the top one tells
 * the reader that something was invalid without telling them what, which is
 * indistinguishable from a bug in this app.
 *
 * Everything specific is kept, in order; the generic wrapper is dropped only
 * when something specific survives it, so this can never render an empty box.
 */
export function xeroErrorLines(messages: readonly string[], fallback: string): string[] {
  const distinct = [...new Set(messages.map((message) => message.trim()).filter(Boolean))];
  const specific = distinct.filter(
    (message) => !XERO_GENERIC_MESSAGES.has(message.toLowerCase()),
  );
  const lines = specific.length > 0 ? specific : distinct;
  return lines.length > 0 ? lines : [fallback];
}
